import { useEffect, useRef, useState } from 'react';
import { DollarSign, FileText, Eye, ChevronDown, ChevronRight, Calendar, Download, Edit3, X, Search, Settings, Send, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useToast } from '../../contexts/ToastContext';
import { downloadDocument } from '../../utils/downloadDocument';
import { fetchStoredBytes } from '../../utils/fileUrls';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';
import ExcelJS from 'exceljs';

// Bytes of a generated invoice PDF. invoice_pdfs keeps file_path (bucket-relative)
// and file_url (old public URL); read by path first, URL as the fallback.
async function fetchInvoicePdfBlob(pdfRecord: { file_path?: string | null; file_url?: string | null }): Promise<Blob> {
  if (pdfRecord.file_path) {
    try {
      return await fetchStoredBytes(pdfRecord.file_path, { bucket: 'company-documents' });
    } catch {
      // fall through to the stored URL
    }
  }
  if (pdfRecord.file_url) return await fetchStoredBytes(pdfRecord.file_url);
  throw new Error('invoice PDF has neither a storage path nor a URL');
}

interface ReadyLoad {
  id: string;
  load_number: string;
  company_name: string;
  driver_name: string;
  invoice_number: string;
  pickup_date: string;
  broker_name: string;
  total_rate: number;
  documents_count: number;
  invoice_sent: boolean;
  invoice_sent_at?: string;
  payment_status: 'pending' | 'billed' | 'paid';
  billing_note?: string;
  is_returned?: boolean;
  carrier?: string;
  trufund_sent?: boolean;
  trufund_sent_at?: string;
  trufund_batch_num?: string;
  trufund_trans_key?: string;
}

interface GeneratedExcel {
  id: string;
  filename: string;
  generated_date: string;
  invoice_count: number;
  total_amount: number;
  invoice_ids: string[];
  carrier?: string;
}

// Sentinel carrierFilter value for the bucket of ready loads whose `carrier`
// text matches no active company. Deliberately unlike any real company name.
const UNASSIGNED_CARRIER = '__unassigned__';

// `loads.carrier` is free text typed at booking time, so it drifts from
// `companies.name`: different casing, stray whitespace, or a legal suffix
// ("CS National" / "CS NATIONAL" / "CS National LLC" are all one carrier).
// Compare on a normalised key — alphanumerics only, trailing entity suffix
// dropped — and accept the company's name or its code, the same reason
// DispatchBoardView's companyForCarrier() matches loosely. Module scope so the
// company-loading effect can reuse it without re-running on every render.
const CARRIER_ENTITY_SUFFIXES = ['llc', 'inc', 'incorporated', 'corp', 'corporation', 'ltd', 'limited', 'llp', 'lp', 'plc', 'company', 'co'];
const normalizeCarrierKey = (value?: string | null) => {
  const tokens = (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
  // Keep the last token when it is all the name has ("Co" as a company name).
  while (tokens.length > 1 && CARRIER_ENTITY_SUFFIXES.includes(tokens[tokens.length - 1])) {
    tokens.pop();
  }
  return tokens.join('');
};

// The load links in this view are plain anchors, so opening a load is a real
// document navigation: the React tree dies and coming back remounts this
// component from scratch. Keep the carrier tab and the active tab in
// sessionStorage so they survive that round trip (and a refresh), the same way
// LoadsView persists its filters. CompactLayout clears storage on sign-out.
const READY_FOR_BILLING_STATE_KEY = 'readyForBillingView:filterState';

type ReadyForBillingPersistedState = {
  carrierFilter: string;
  activeTab: 'ready' | 'invoices' | 'excels';
};

function readPersistedReadyForBillingState(): Partial<ReadyForBillingPersistedState> {
  try {
    const raw = sessionStorage.getItem(READY_FOR_BILLING_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function ReadyForBillingView() {
  const toast = useToast();
  const persisted = readPersistedReadyForBillingState();
  // Captured once at mount: the company load below must be able to tell a
  // restored selection from a cold start, and a ref keeps it out of the
  // effect's dependencies.
  const restoredCarrierRef = useRef(persisted.carrierFilter);
  const [loading, setLoading] = useState(true);
  const [loads, setLoads] = useState<ReadyLoad[]>([]);
  const [searchTerm, setSearchTerm] = useState(() => {
    // TabelaView links here as /invoices?search=<invoice_number>. An explicit
    // search in the URL is the caller's intent, so it wins over anything
    // restored from storage.
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || '';
  });
  const [readyStatusFilter, setReadyStatusFilter] = useState<'all' | 'ready' | 'returned'>('all');
  const [documentModal, setDocumentModal] = useState<{ loadId: string; documents: any[] } | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ready' | 'invoices' | 'excels'>(
    persisted.activeTab === 'invoices' || persisted.activeTab === 'excels' ? persisted.activeTab : 'ready'
  );
  const [carrierFilter, setCarrierFilter] = useState<string>(persisted.carrierFilter ?? '');
  const [companies, setCompanies] = useState<Array<{id: string; name: string; code: string; factoring_info?: string | null}>>([]);
  const [trufundConfigured, setTrufundConfigured] = useState(false);

  // Load companies from database
  useEffect(() => {
    async function loadCompanies() {
      const { data } = await supabase
        .from('companies')
        .select('id, name, code, factoring_info')
        .eq('is_active', true)
        .order('name');

      if (data && data.length > 0) {
        setCompanies(data);
        // Don't clobber a carrier restored from sessionStorage. It is stored by
        // name, so it only counts if it still matches an active company — one
        // could have been renamed or deactivated since — while the Unassigned
        // sentinel is a legitimate selection of its own. Otherwise seed the
        // first company, as before.
        const restored = restoredCarrierRef.current;
        const restoredIsValid =
          restored === UNASSIGNED_CARRIER ||
          (!!restored && data.some((c) => normalizeCarrierKey(c.name) === normalizeCarrierKey(restored)));
        if (!restoredIsValid) {
          setCarrierFilter(data[0].name);
        }
      }
    }
    async function loadTrufundConfig() {
      // Use the status-only edge function — accounting users can't read the
      // locked system_settings table directly.
      const { data } = await supabase.functions.invoke('integration-status');
      const t = data?.status?.trufund;
      setTrufundConfigured(!!(t?.connected && t?.enabled));
    }
    loadCompanies();
    loadTrufundConfig();
  }, []);

  const companyForCarrier = (carrier?: string | null) => {
    const key = normalizeCarrierKey(carrier);
    if (!key) return undefined;
    return companies.find(
      (c) => normalizeCarrierKey(c.name) === key || normalizeCarrierKey(c.code) === key
    );
  };

  // Does this load belong under the carrier tab currently selected? Loads that
  // resolve to no company live in the Unassigned bucket rather than nowhere.
  const loadMatchesCarrierFilter = (load: ReadyLoad) => {
    const company = companyForCarrier(load.carrier);
    if (carrierFilter === UNASSIGNED_CARRIER) return !company;
    return !!company && normalizeCarrierKey(company.name) === normalizeCarrierKey(carrierFilter);
  };
  const isUnassignedCarrier = carrierFilter === UNASSIGNED_CARRIER;

  // True when the currently-selected carrier company has TruFund / TruFunding
  // mentioned in its Factoring text on /companies. Battle Express + CS National
  // use it; DN Expedited does not.
  const currentCarrierUsesTruFund = (() => {
    const company = companies.find((c) => c.name === carrierFilter);
    const text = (company?.factoring_info || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return text.includes('trufund') || text.includes('trufunding');
  })();
  const showTruFundButton = trufundConfigured && currentCarrierUsesTruFund;
  const [selectedLoads, setSelectedLoads] = useState<Set<string>>(new Set());
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [invoicesInExcel, setInvoicesInExcel] = useState<Set<string>>(new Set());
  const [invoiceCompanyTypes, setInvoiceCompanyTypes] = useState<Map<string, string>>(new Map());
  const [invoiceModal, setInvoiceModal] = useState<{ loads: ReadyLoad[]; customer: string; documents: any[]; companyType?: string; loadsByBroker?: Map<string, ReadyLoad[]> } | null>(null);
  const [selectedCustomerForInvoice, setSelectedCustomerForInvoice] = useState<string>('');
  const [allCustomers, setAllCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState<string>('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState<boolean>(false);
  const [editedInvoiceAmounts, setEditedInvoiceAmounts] = useState<Map<string, number>>(new Map());
  // Edited invoice numbers per load, plus live conflict flags keyed by load id.
  // A conflict means another load (excluding this one) already owns that invoice_number.
  const [editedInvoiceNumbers, setEditedInvoiceNumbers] = useState<Map<string, string>>(new Map());
  const [invoiceNumberConflicts, setInvoiceNumberConflicts] = useState<Map<string, 'checking' | 'conflict' | 'ok'>>(new Map());
  const invoiceCheckTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Debounced live check against loads.invoice_number for a duplicate.
  // Runs 400ms after the user stops typing.
  const scheduleInvoiceNumberCheck = (loadId: string, value: string) => {
    const prev = invoiceCheckTimers.current.get(loadId);
    if (prev) clearTimeout(prev);
    const trimmed = value.trim();
    if (!trimmed) {
      setInvoiceNumberConflicts(m => {
        const n = new Map(m);
        n.delete(loadId);
        return n;
      });
      return;
    }
    setInvoiceNumberConflicts(m => new Map(m).set(loadId, 'checking'));
    const t = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from('loads')
          .select('id')
          .eq('invoice_number', trimmed)
          .neq('id', loadId)
          .limit(1);
        if (error) throw error;
        setInvoiceNumberConflicts(m => new Map(m).set(loadId, (data && data.length > 0) ? 'conflict' : 'ok'));
      } catch {
        // On network/query failure leave prior status in place.
      }
    }, 400);
    invoiceCheckTimers.current.set(loadId, t);
  };
  const [selectedCustomersPerLoad, setSelectedCustomersPerLoad] = useState<Map<string, string>>(new Map());
  const [customerSearchPerLoad, setCustomerSearchPerLoad] = useState<Map<string, string>>(new Map());
  const [showDropdownPerLoad, setShowDropdownPerLoad] = useState<Map<string, boolean>>(new Map());
  const [invoicedLoads, setInvoicedLoads] = useState<ReadyLoad[]>([]);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; label: string } | null>(null);
  const [groupingMode, setGroupingMode] = useState<'week' | 'month'>('week');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());
  const [noteModal, setNoteModal] = useState<{ loadId: string; currentNote: string } | null>(null);
  const [generatedExcels, setGeneratedExcels] = useState<GeneratedExcel[]>([]);
  const [excelPreviewModal, setExcelPreviewModal] = useState<{ filename: string; data: any[] } | null>(null);
  const [invoicesSearchTerm, setInvoicesSearchTerm] = useState('');
  const [invoicesStatusFilter, setInvoicesStatusFilter] = useState<'all' | 'returned' | 'billed' | 'paid'>('all');
  const [excelsSearchTerm, setExcelsSearchTerm] = useState('');
  const [excelsGroupingMode, setExcelsGroupingMode] = useState<'week' | 'month'>('week');
  const [expandedExcelGroups, setExpandedExcelGroups] = useState<Set<string>>(new Set());
  const [loadDetailsModal, setLoadDetailsModal] = useState<ReadyLoad | null>(null);
  const [generatedInvoicePdfs, setGeneratedInvoicePdfs] = useState<any[]>([]);
  const [invoicesGroupingMode, setInvoicesGroupingMode] = useState<'week' | 'month'>('week');
  const [returnedInvoices, setReturnedInvoices] = useState<Set<string>>(new Set());
  const [companyInfo, setCompanyInfo] = useState<Record<string, { id: string; name: string; address: string; city: string; state: string; zip: string; country: string; factoringInfo: string }>>({});
  const [availableCompanies, setAvailableCompanies] = useState<Array<{ id: string; name: string; code: string; invoiceNumberStart: number | null }>>([]);
  const [showInvoiceRangeSettings, setShowInvoiceRangeSettings] = useState(false);
  const [rangeDraft, setRangeDraft] = useState<Record<string, string>>({});
  const [savingRanges, setSavingRanges] = useState(false);
  const [invoiceStats, setInvoiceStats] = useState<Record<string, { count: number; last: string | null }>>({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [trufundSending, setTrufundSending] = useState(false);
  const [trufundProgress, setTrufundProgress] = useState<{ current: number; total: number; label: string } | null>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(READY_FOR_BILLING_STATE_KEY, JSON.stringify({
        carrierFilter,
        activeTab,
      }));
    } catch {
      // ignore quota / privacy-mode errors
    }
  }, [carrierFilter, activeTab]);

  useEffect(() => {
    loadReadyLoads();
    loadCompanyInfo();
    loadGeneratedExcels();
    loadGeneratedInvoicePdfs();
    loadAllCustomers();
    if (activeTab === 'invoices' || activeTab === 'excels') {
      loadInvoicedLoads();
    }
  }, [activeTab]);

  async function loadAllCustomers() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setAllCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }

  async function loadGeneratedInvoicePdfs() {
    try {
      const { data, error } = await supabase
        .from('invoice_pdfs')
        .select('*')
        .order('generated_date', { ascending: false });

      if (error) {
        console.log('Invoice PDFs table not found, will create records when needed');
        return;
      }

      setGeneratedInvoicePdfs(data || []);
    } catch (error) {
      console.error('Error loading invoice PDFs:', error);
    }
  }

  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function getMonthStart(date: Date): Date {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function formatWeekRange(weekStart: string): string {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    
    return `Week of ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  function formatMonthRange(monthStart: string): string {
    const date = new Date(monthStart);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  async function loadGeneratedExcels() {
    try {
      const { data, error } = await supabase
        .from('generated_excels')
        .select('*')
        .order('generated_date', { ascending: false });

      if (error) {
        // Table might not exist yet, that's okay
        console.log('Generated excels table not found, will create records when needed');
        return;
      }

      setGeneratedExcels(data || []);
    } catch (error) {
      console.error('Error loading generated excels:', error);
    }
  }

  async function loadCompanyInfo() {
    try {
      const { data: allCompanies, error: allError } = await supabase
        .from('companies')
        .select('id, name, code, address, city, state, zip_code, factoring_info, invoice_number_start')
        .eq('is_active', true)
        // Ordered on purpose: availableCompanies' index is what the legacy
        // invoice range layout (1000 + index * 100000) keys off, so an
        // unordered query would hand a company a different range per page load.
        .order('name');

      console.log('📋 All companies in database:', allCompanies);

      if (allError) throw allError;

      if (allCompanies && allCompanies.length > 0) {
        // Build company info object dynamically
        const companiesMap: Record<string, { id: string; name: string; address: string; city: string; state: string; zip: string; country: string; factoringInfo: string }> = {};
        const companiesList: Array<{ id: string; name: string; code: string; invoiceNumberStart: number | null }> = [];
        
        allCompanies.forEach(company => {
          const companyKey = (company.code || company.name).toLowerCase().replace(/[^a-z0-9]/g, '');
          companiesMap[companyKey] = {
            id: company.id,
            name: company.name.toUpperCase(),
            address: company.address || '',
            city: company.city || '',
            state: company.state || '',
            zip: company.zip_code || '',
            country: 'United States',
            factoringInfo: (company as any).factoring_info || ''
          };
          companiesList.push({
            id: company.id,
            name: company.name,
            code: company.code || company.name,
            invoiceNumberStart: (company as any).invoice_number_start ?? null
          });
        });
        
        setCompanyInfo(companiesMap);
        setAvailableCompanies(companiesList);
        console.log('✅ Loaded companies:', companiesList.map(c => c.name));
      } else {
        console.warn('⚠️ No companies found in database. Please add companies in Safety Management > Companies');
      }
    } catch (error) {
      console.error('❌ Error loading company info:', error);
    }
  }

  // Fetch invoice count + last number per company from the actual
  // loads → dispatches → drivers → companies relation.
  async function loadInvoiceStats() {
    if (availableCompanies.length === 0) return;
    setLoadingStats(true);
    try {
      const data = await fetchAllRows<any>(() => supabase
        .from('loads')
        .select(`
          id,
          invoice_number,
          dispatches!dispatches_load_id_fkey (
            drivers!dispatches_driver_id_fkey (
              company_id
            )
          )
        `)
        .not('invoice_number', 'is', null));

      const stats: Record<string, { count: number; last: string | null }> = {};
      availableCompanies.forEach(c => {
        stats[c.id] = { count: 0, last: null };
      });

      // "Last invoice #" sits next to "Starting #" and is read as "where the
      // sequence is now", so it has to be the number generateNextInvoiceNumber
      // will actually continue from. That means applying the same window the
      // generator uses: a hand-typed reference far outside the company's range
      // (CS National had 69191134 on a real load) is a genuine invoice number,
      // but it is not part of the sequence, and showing it there is what made
      // the numbering look broken when it was the display that disagreed.
      const statRanges = deriveInvoiceNumberRanges();
      const rangeByCompanyId = new Map<string, { min: number; max: number }>();
      availableCompanies.forEach(c => {
        const range = statRanges[invoiceRangeKey(c)];
        if (range) rangeByCompanyId.set(c.id, range);
      });

      (data || []).forEach((row: any) => {
        const dispatches = row.dispatches || [];
        const companyId = dispatches[0]?.drivers?.company_id as string | undefined;
        if (!companyId || !stats[companyId]) return;
        // Only numbers this app issued, so a broker reference typed into the
        // free-text invoice field can't be reported back as the last number
        // generated - the panel has to agree with generateNextInvoiceNumber.
        const parsed = parseOwnInvoiceNumber(row.invoice_number);
        if (!parsed) return;
        // Count every invoice the company really has, in or out of range.
        stats[companyId].count += 1;
        // ...but only in-range numbers can be the sequence's last value.
        const companyRange = rangeByCompanyId.get(companyId);
        if (companyRange && (parsed.value < companyRange.min || parsed.value > companyRange.max)) {
          return;
        }
        const previous = stats[companyId].last;
        const previousParsed = previous ? parseOwnInvoiceNumber(previous) : null;
        // Later prefix generation wins first, then the higher number within it.
        const isLater =
          !previousParsed ||
          compareInvoicePrefixes(parsed.prefix, previousParsed.prefix) > 0 ||
          (parsed.prefix === previousParsed.prefix && parsed.value > previousParsed.value);
        if (isLater) {
          stats[companyId].last = `${parsed.prefix}${parsed.value}`;
        }
      });

      setInvoiceStats(stats);
    } catch (err) {
      console.error('Failed to load invoice stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }

  // Helper function to get week number and year
  const getWeekKey = (date: string) => {
    const d = new Date(date);
    const firstDayOfYear = new Date(d.getFullYear(), 0, 1);
    const pastDaysOfYear = (d.getTime() - firstDayOfYear.getTime()) / 86400000;
    const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    return `${d.getFullYear()}-W${weekNumber.toString().padStart(2, '0')}`;
  };

  // Helper function to get month key
  const getMonthKey = (date: string) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  // Helper function to format period label
  const formatPeriodLabel = (key: string, mode: 'week' | 'month') => {
    if (mode === 'week') {
      const [year, week] = key.split('-W');
      return `Week ${week}, ${year}`;
    } else {
      const [year, month] = key.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('en-US', { month: 'long' });
      return `${monthName} ${year}`;
    }
  };

  // Group invoices by driver and time period
  const groupInvoices = (loads: ReadyLoad[], mode: 'week' | 'month') => {
    const groups: Record<string, Record<string, ReadyLoad[]>> = {};
    
    loads.forEach(load => {
      const driverName = load.driver_name;
      const periodKey = mode === 'week' 
        ? getWeekKey(load.pickup_date) 
        : getMonthKey(load.pickup_date);
      
      if (!groups[driverName]) {
        groups[driverName] = {};
      }
      if (!groups[driverName][periodKey]) {
        groups[driverName][periodKey] = [];
      }
      groups[driverName][periodKey].push(load);
    });
    
    return groups;
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleAllGroups = (expand: boolean) => {
    if (expand) {
      const allKeys: string[] = [];
      const grouped = groupInvoices(invoicedLoads, groupingMode);
      Object.keys(grouped).forEach(driver => {
        Object.keys(grouped[driver]).forEach(period => {
          allKeys.push(`${driver}-${period}`);
        });
      });
      setExpandedGroups(new Set(allKeys));
    } else {
      setExpandedGroups(new Set());
    }
  };

  // Group loads by invoice date
  const groupLoadsByInvoice = (loads: ReadyLoad[]) => {
    const groups: Record<string, ReadyLoad[]> = {};
    loads.forEach(load => {
      const invoiceDate = load.invoice_sent_at 
        ? new Date(load.invoice_sent_at).toLocaleDateString() 
        : 'No Date';
      if (!groups[invoiceDate]) {
        groups[invoiceDate] = [];
      }
      groups[invoiceDate].push(load);
    });
    return groups;
  };

  const toggleInvoice = (invoiceNumber: string) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceNumber)) {
      newExpanded.delete(invoiceNumber);
    } else {
      newExpanded.add(invoiceNumber);
    }
    setExpandedInvoices(newExpanded);
  };

  // Generate Excel/CSV export for selected invoices
  const generateExcelExport = async () => {
    if (selectedInvoices.size === 0) {
      toast.error('Please select at least one invoice');
      return;
    }

    // Get selected loads
    const selectedLoads = invoicedLoads.filter(load => selectedInvoices.has(load.id));

    // Sort by invoice number (smallest to largest)
    selectedLoads.sort((a, b) => {
      const numA = parseInt(a.invoice_number) || 0;
      const numB = parseInt(b.invoice_number) || 0;
      return numA - numB;
    });

    // Calculate total amount
    const totalAmount = selectedLoads.reduce((sum, load) => sum + load.total_rate, 0);

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Invoices');

    // Set column widths
    worksheet.columns = [
      { width: 35 }, // DTR_NAME
      { width: 25 }, // INVOICE#
      { width: 12 }, // INV_DATE
      { width: 20 }, // PO
      { width: 12 }  // INVAMT
    ];

    // Row 1: Headers
    const headerRow = worksheet.getRow(1);
    headerRow.values = ['DTR_NAME', 'INVOICE#', 'INV_DATE', 'PO', 'INVAMT'];
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Row 2+: Data rows
    let currentRow = 2;
    selectedLoads.forEach(load => {
      const row = worksheet.getRow(currentRow);
      row.values = [
        load.broker_name || '',
        load.invoice_number || '',
        new Date().toLocaleDateString('en-US'),
        load.load_number || '',
        load.total_rate || 0
      ];
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle' };
        // Right align the amount column
        if (colNumber === 5) {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
      });
      currentRow++;
    });

    // Add empty rows to reach at least 25 data rows
    const minRows = 25;
    while (currentRow <= minRows + 1) {
      const row = worksheet.getRow(currentRow);
      row.values = ['', '', '', '', ''];
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
      currentRow++;
    }

    // Add summary row
    const summaryRow = worksheet.getRow(currentRow);
    summaryRow.values = [selectedLoads.length, 'INVOICES - THIS SCHEDULE:', '', '', totalAmount];
    summaryRow.getCell(1).font = { bold: true };
    summaryRow.getCell(1).alignment = { horizontal: 'center' };
    summaryRow.getCell(2).font = { bold: true };
    summaryRow.getCell(2).alignment = { horizontal: 'center' };
    summaryRow.getCell(5).font = { bold: true };
    summaryRow.getCell(5).alignment = { horizontal: 'right' };
    summaryRow.getCell(5).border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    currentRow++;

    // Add empty row
    currentRow++;

    // Add legal text
    const legalText = "For valuable consideration, receipt of which is hereby acknowledged, the undersigned hereby SELLS, assigns, sets over and transfers to TBK Bank, SSB d/b/a Triumph (\"TRIUMPH\"), its successors or assigns, all its rights, title and interest in and to the invoices listed above, including all monies due or to become due thereon, all in accordance with and pursuant to the Factoring and Security Agreement now existing by and between the undersigned and TRIUMPH, the conditions, representations, warranties and agreements of which are made part of this SALE and assignment and incorporated herein by reference.";
    const legalRow = worksheet.getRow(currentRow);
    legalRow.getCell(1).value = legalText;
    legalRow.getCell(1).font = { size: 9 };
    legalRow.getCell(1).alignment = { wrapText: true, vertical: 'top' };
    worksheet.mergeCells(currentRow, 1, currentRow, 5);
    legalRow.height = 60;
    currentRow++;

    // Add company name label
    const companyLabelRow = worksheet.getRow(currentRow);
    companyLabelRow.getCell(1).value = 'Company Name';
    companyLabelRow.getCell(1).font = { bold: true };
    currentRow++;

    // Add company name input box
    const companyInputRow = worksheet.getRow(currentRow);
    companyInputRow.getCell(1).value = '';
    worksheet.mergeCells(currentRow, 1, currentRow, 3);
    companyInputRow.getCell(1).border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    currentRow++;

    // Add authorization signature label
    const sigLabelRow = worksheet.getRow(currentRow);
    sigLabelRow.getCell(1).value = 'Authorization Signature';
    sigLabelRow.getCell(1).font = { bold: true };
    currentRow++;

    // Add signature input boxes
    const sigInputRow = worksheet.getRow(currentRow);
    sigInputRow.getCell(1).value = '';
    sigInputRow.getCell(4).value = 'Date of Assignment';
    sigInputRow.getCell(4).font = { bold: true };
    worksheet.mergeCells(currentRow, 1, currentRow, 3);
    worksheet.mergeCells(currentRow, 4, currentRow, 5);
    sigInputRow.getCell(1).border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    sigInputRow.getCell(4).border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Format filename as CarrierName_MM-DD-YYYY
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    const filename = `${carrierFilter}_${month}-${day}-${year}.xlsx`;

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);

    // Save to database
    const saveToDatabase = async () => {
      try {
        const { data, error } = await supabase
          .from('generated_excels')
          .insert([{
            file_name: filename,
            filename: filename,
            generated_date: now.toISOString(),
            invoice_count: selectedLoads.length,
            total_amount: totalAmount,
            invoice_ids: Array.from(selectedInvoices),
            carrier: carrierFilter
          }])
          .select()
          .single();

        if (error) throw error;

        // Add to local state
        if (data) {
          setGeneratedExcels(prev => [data, ...prev]);
          const { emailReminders } = await import('../../services/emailReminders');
          void emailReminders.invoiceSent({
            id: data.id, filename, invoice_count: selectedLoads.length,
            total_amount: totalAmount, carrier: carrierFilter,
          });
        }
      } catch (error) {
        console.error('Error saving Excel record to database:', error);
        toast.error('Excel generated but failed to save record');
      }
    };

    saveToDatabase();

    // Mark selected invoices as billed
    const updatePromises = selectedLoads.map(load => 
      supabase
        .from('loads')
        .update({ payment_status: 'billed' })
        .eq('id', load.id)
    );

    Promise.all(updatePromises).then(() => {
      loadInvoicedLoads(); // Reload to show updated status
    }).catch(error => {
      console.error('Error updating payment status:', error);
    });

    // Mark selected invoices as used in Excel
    const newInvoicesInExcel = new Set(invoicesInExcel);
    selectedInvoices.forEach(id => newInvoicesInExcel.add(id));
    setInvoicesInExcel(newInvoicesInExcel);

    toast.success(`Excel file "${filename}" generated with ${selectedInvoices.size} invoice(s)!`);

    // Clear selection after generation
    setSelectedInvoices(new Set());

    // Switch to Generated Excels tab
    setActiveTab('excels');
  };

  // Build the TruFund-format Excel summary for selected invoiced loads.
  // Spec columns: DEBNAME, INV_ID, INV_DATE (MM.DD.YYYY), PO_NO, INVAMT — all text.
  const buildTruFundExcel = async (loads: ReadyLoad[]): Promise<ArrayBuffer> => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Invoices');
    ws.columns = [
      { width: 35 },
      { width: 15 },
      { width: 12 },
      { width: 18 },
      { width: 12 },
    ];
    const header = ws.getRow(1);
    header.values = ['DEBNAME', 'INV_ID', 'INV_DATE', 'PO_NO', 'INVAMT'];
    header.font = { bold: true };

    let r = 2;
    for (const load of loads) {
      const dateSrc = load.invoice_sent_at || load.pickup_date;
      const d = dateSrc ? new Date(dateSrc) : new Date();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      const invDate = `${mm}.${dd}.${yyyy}`;
      const amount = (Number(load.total_rate) || 0).toFixed(2);

      const row = ws.getRow(r++);
      row.values = [
        load.broker_name || '',
        load.invoice_number || '',
        invDate,
        load.load_number || '',
        amount,
      ];
      // TruFund requires every cell to be formatted as text.
      row.eachCell((cell) => {
        cell.numFmt = '@';
        if (cell.value != null) cell.value = String(cell.value);
      });
    }
    header.eachCell((cell) => { cell.numFmt = '@'; });
    return await wb.xlsx.writeBuffer() as ArrayBuffer;
  };

  // Send selected invoiced loads to TruFund as a single shipment.
  // Order: 1) Excel summary, 2) generated invoice PDF (if any), 3) supporting load documents.
  const sendSelectedToTruFund = async () => {
    if (selectedInvoices.size === 0) {
      toast.error('Please select at least one invoice');
      return;
    }

    // Confirm if any selected invoices were already sent.
    const selectedLoads = invoicedLoads.filter((l) => selectedInvoices.has(l.id));
    selectedLoads.sort((a, b) => (parseInt(a.invoice_number) || 0) - (parseInt(b.invoice_number) || 0));
    const alreadySent = selectedLoads.filter((l) => l.trufund_sent);
    if (alreadySent.length > 0) {
      const ok = window.confirm(
        `${alreadySent.length} of ${selectedLoads.length} selected invoice(s) were already sent to TruFund. Re-send anyway?`
      );
      if (!ok) return;
    }

    // Verify TruFund is configured/enabled via the status-only edge function
    // (the credentials themselves are read server-side by the trufund-upload
    // function; accounting users can't read system_settings directly).
    const { data: statusData } = await supabase.functions.invoke('integration-status');
    const trufundStatus = statusData?.status?.trufund;
    if (!trufundStatus?.connected) {
      toast.error('TruFund is not configured. Open Settings → Integrations to add credentials.');
      return;
    }
    if (!trufundStatus?.enabled) {
      toast.error('TruFund integration is disabled. Enable it in Settings → Integrations.');
      return;
    }

    setTrufundSending(true);
    try {
      const { uploadToTruFund, arrayBufferToBase64, sanitizeFileName, fileToBase64 } = await import(
        '../../services/trufundService'
      );

      // 1) Build Excel summary (file 1).
      const excelBuf = await buildTruFundExcel(selectedLoads);
      const excelB64 = arrayBufferToBase64(excelBuf);
      const stamp = new Date();
      const stampStr = `${stamp.getFullYear()}${String(stamp.getMonth() + 1).padStart(2, '0')}${String(stamp.getDate()).padStart(2, '0')}_${String(stamp.getHours()).padStart(2, '0')}${String(stamp.getMinutes()).padStart(2, '0')}${String(stamp.getSeconds()).padStart(2, '0')}`;
      const excelName = sanitizeFileName(`${carrierFilter || 'invoices'}_${stampStr}.xlsx`);

      // 2) Gather supporting files (invoice PDF + load documents) for each load.
      type Entry = { name: string; b64: string; loadId: string };
      const supporting: Entry[] = [];
      // Files over the 15MB cap are dropped from the package - collect them so
      // the user is told instead of only the console
      const skipped: string[] = [];

      for (const load of selectedLoads) {
        // Invoice PDF (if generated and stored).
        const pdfRecord = generatedInvoicePdfs.find((p) => p.load_id === load.id);
        if (pdfRecord?.file_path || pdfRecord?.file_url) {
          try {
            const blob = await fetchInvoicePdfBlob(pdfRecord);
            const buf = await blob.arrayBuffer();
            const fname = sanitizeFileName(`Invoice_${load.invoice_number || load.load_number}.pdf`);
            supporting.push({ name: fname, b64: arrayBufferToBase64(buf), loadId: load.id });
          } catch {
            skipped.push(`Invoice PDF (load ${load.load_number}) could not be read`);
          }
        }

        // Load documents (BOL / RC / POD ...).
        const { data: docs } = await supabase
          .from('load_documents')
          .select(`
            id, document_type, document_name, file_url, display_order,
            documents ( id, name, file_path, file_size, file_type, mime_type )
          `)
          .eq('load_id', load.id)
          .order('display_order', { ascending: true, nullsFirst: false })
          .order('uploaded_at', { ascending: false });

        for (const doc of docs || []) {
          const docData: any = Array.isArray((doc as any).documents)
            ? (doc as any).documents[0]
            : (doc as any).documents;
          const filePath = docData?.file_path || (doc as any).file_url;
          if (!filePath) continue;
          const { data: signed } = await supabase.storage
            .from('company-documents')
            .createSignedUrl(filePath, 3600);
          if (!signed?.signedUrl) continue;
          try {
            const resp = await fetch(signed.signedUrl);
            if (!resp.ok) continue;
            const blob = await resp.blob();
            if (blob.size > 15 * 1024 * 1024) {
              const skippedName = docData?.name || (doc as any).document_name || filePath;
              console.warn(`Skipping ${skippedName}: exceeds 15MB`);
              skipped.push(`${skippedName} (load ${load.load_number})`);
              continue;
            }
            const ext = (docData?.file_type || (filePath.split('.').pop() || 'pdf')).toLowerCase();
            const baseName = docData?.name || (doc as any).document_name || `doc_${(doc as any).id}`;
            const cleanBase = sanitizeFileName(baseName.replace(/\.[^.]+$/, ''));
            const fname = `${cleanBase}_${load.load_number}.${ext}`;
            supporting.push({
              name: sanitizeFileName(fname),
              b64: await fileToBase64(blob),
              loadId: load.id,
            });
          } catch (e) {
            console.warn('Could not fetch document', filePath, e);
          }
        }
      }

      const totalFiles = 1 + supporting.length;
      const shipmentNum = `${stampStr}-${Math.random().toString(36).substring(2, 8)}`;
      const extraInfo = `Carrier=${carrierFilter || ''}; Invoices=${selectedLoads.map((l) => l.invoice_number).join(',')}`;

      // 3) Send Excel as file 1.
      setTrufundProgress({ current: 1, total: totalFiles, label: excelName });
      const excelResult = await uploadToTruFund({
        shipmentNum,
        fileName: excelName,
        base64String: excelB64,
        sequenceNumber: 1,
        totalFiles,
        extraInfo,
      });
      if (!excelResult.success) {
        throw new Error(`File 1/${totalFiles} (${excelName}) failed: ${excelResult.error || excelResult.extra_info || 'Unknown'}`);
      }

      // 4) Send each supporting file in order. The trigger fires on the final file.
      let lastResult = excelResult;
      for (let i = 0; i < supporting.length; i++) {
        const file = supporting[i];
        const seq = i + 2;
        setTrufundProgress({ current: seq, total: totalFiles, label: file.name });
        const res = await uploadToTruFund({
          shipmentNum,
          fileName: file.name,
          base64String: file.b64,
          sequenceNumber: seq,
          totalFiles,
          extraInfo,
        });
        if (!res.success) {
          throw new Error(`File ${seq}/${totalFiles} (${file.name}) failed: ${res.error || res.extra_info || 'Unknown'}`);
        }
        lastResult = res;
      }

      // 5) Mark loads as sent and store batch/transaction keys returned for the final file.
      const transKey = lastResult.trans_key != null ? String(lastResult.trans_key) : null;
      const batchNum = lastResult.batch_num != null ? String(lastResult.batch_num) : null;
      const sentAt = new Date().toISOString();
      await Promise.all(
        selectedLoads.map((load) =>
          supabase
            .from('loads')
            .update({
              trufund_sent: true,
              trufund_sent_at: sentAt,
              trufund_shipment_num: shipmentNum,
              trufund_batch_num: batchNum,
              trufund_trans_key: transKey,
            })
            .eq('id', load.id)
        )
      );

      toast.success(
        `Sent ${totalFiles} file(s) to TruFund. Batch ${batchNum ?? '—'} / Trans ${transKey ?? '—'}`
      );
      if (skipped.length > 0) {
        toast.warning(
          `${skipped.length} document(s) were NOT sent (over the 15MB limit or could not be read): ${skipped.join(', ')}`,
          15000
        );
      }
      setSelectedInvoices(new Set());
      loadInvoicedLoads();
    } catch (error: any) {
      console.error('TruFund send error:', error);
      toast.error(error?.message || 'Failed to send to TruFund');
    } finally {
      setTrufundSending(false);
      setTrufundProgress(null);
    }
  };

  // Generate PDF for a single invoice
  const generateInvoicePDF = async (loads: ReadyLoad[], companyType: string = Object.keys(companyInfo)[0]): Promise<Blob> => {
    const doc = new jsPDF();
    const company = companyInfo[companyType];
    
    if (!company) {
      throw new Error(`Company info not available for ${companyType}`);
    }

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(company.name, 14, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(company.address, 14, 28);
    doc.text(`${company.city}, ${company.state} ${company.zip}`, 14, 34);
    doc.text(company.country, 14, 40);

    // Invoice details
    const invoiceNum = loads[0]?.invoice_number || 'TBD';
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Invoice: ${invoiceNum}`, 200, 20, { align: 'right' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 200, 28, { align: 'right' });

    // Table
    const tableData = loads.map(load => [
      load.load_number,
      load.driver_name,
      load.pickup_date ? new Date(load.pickup_date).toLocaleDateString() : 'N/A',
      load.broker_name,
      `$${load.total_rate.toFixed(2)}`
    ]);

    const totalAmount = loads.reduce((sum, load) => sum + load.total_rate, 0);

    autoTable(doc, {
      startY: 50,
      head: [['Load #', 'Driver', 'Pickup Date', 'Broker', 'Amount']],
      body: tableData,
      foot: [['', '', '', 'Total:', `$${totalAmount.toFixed(2)}`]],
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] },
      footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold' },
    });

    const finalY = (doc as any).lastAutoTable.finalY || 100;
    
    // Add "Thank you for your business" first
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('THANK YOU FOR YOUR BUSINESS!', 105, finalY + 20, { align: 'center' });

    // Add Notice of Assignment from the company's factoring_info; skipped when blank
    const factoringLines = (company?.factoringInfo || '')
      .split('\n')
      .map(l => l.trimEnd())
      .filter(l => l.length > 0);
    if (factoringLines.length > 0) {
      const noticeStartY = finalY + 35;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('NOTICE OF ASSIGNMENT', 105, noticeStartY, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('THIS INVOICE HAS BEEN ASSIGNED TO,', 105, noticeStartY + 6, { align: 'center' });
      doc.text('AND MUST BE PAID DIRECTLY TO:', 105, noticeStartY + 12, { align: 'center' });

      let lineY = noticeStartY + 20;
      factoringLines.forEach((line, idx) => {
        doc.setFont('helvetica', idx === 0 ? 'bold' : 'normal');
        doc.text(line, 105, lineY, { align: 'center' });
        lineY += 6;
      });

      doc.setFont('helvetica', 'bold');
      doc.text('THANK YOU FOR YOUR BUSINESS!', 105, lineY + 6, { align: 'center' });
    }

    return doc.output('blob');
  };

  // An invoice number is either a plain number ('4999') or a number carrying a
  // spreadsheet-style letter prefix ('A1000', 'AA1000'). The prefix is what lets
  // a company keep issuing invoices once its numeric range is exhausted, without
  // spilling into the next company's range. Returns null for anything that is
  // neither shape ('TBD', '', 'INV-2026-01').
  type ParsedInvoiceNumber = { prefix: string; value: number };

  const parseInvoiceNumber = (raw: string | null | undefined): ParsedInvoiceNumber | null => {
    const match = /^([A-Z]*)(\d+)$/.exec((raw ?? '').trim().toUpperCase());
    if (!match) return null;
    const value = parseInt(match[2], 10);
    if (isNaN(value)) return null;
    return { prefix: match[1], value };
  };

  // The prefixes this app can ever generate are '' (the original run) and one
  // or two letters ('A'..'Z', 'AA'..'ZZ') - a company would have to burn
  // 67 billion numbers to need a third letter. Anything longer is a broker or
  // factoring reference that merely fits the shape: 'LDS15529403' parses as
  // prefix 'LDS' + 15529403, and because compareInvoicePrefixes ranks a longer
  // prefix as a later generation it would outrank every real number and make
  // the generator emit 'LDS15529404'. Such values are not ours to step from.
  const isOurInvoicePrefix = (prefix: string): boolean => prefix.length <= 2;

  // parseInvoiceNumber restricted to numbers this app issued. Everything that
  // picks a "highest so far" must go through this and not parseInvoiceNumber,
  // or the generator, the batch stepper and the stats panel disagree about
  // which values count.
  const parseOwnInvoiceNumber = (raw: string | null | undefined): ParsedInvoiceNumber | null => {
    const parsed = parseInvoiceNumber(raw);
    return parsed && isOurInvoicePrefix(parsed.prefix) ? parsed : null;
  };

  // The key a company is known by in the invoice range map, and the same string
  // the call sites build from carrierFilter.
  const invoiceRangeKey = (company: { name: string; code?: string | null }) =>
    (company.code || company.name).toLowerCase().replace(/[^a-z0-9]/g, '');

  // Next prefix generation, spreadsheet-column style so it never runs out:
  // '' -> 'A' -> 'B' -> ... -> 'Z' -> 'AA' -> 'AB' -> ... -> 'ZZ' -> 'AAA'.
  const nextInvoicePrefix = (prefix: string): string => {
    if (!prefix) return 'A';
    const chars = prefix.split('');
    let i = chars.length - 1;
    for (; i >= 0; i--) {
      if (chars[i] === 'Z') {
        chars[i] = 'A'; // carry one place to the left
      } else {
        chars[i] = String.fromCharCode(chars[i].charCodeAt(0) + 1);
        break;
      }
    }
    // Carried past the leftmost character ('Z' -> 'AA'): grow by one place.
    return i < 0 ? `A${chars.join('')}` : chars.join('');
  };

  // Generation order: '' (the original, unprefixed run) is the oldest, then
  // A..Z, then AA.. - a longer prefix is always a later generation, so compare
  // length before comparing letters.
  const compareInvoicePrefixes = (a: string, b: string): number =>
    a.length !== b.length ? a.length - b.length : a.localeCompare(b);

  // Successor of an invoice number inside a range: +1 while there is room,
  // otherwise restart at the bottom of the same range under the next prefix.
  const nextInvoiceNumberAfter = (
    current: ParsedInvoiceNumber,
    range: { min: number; max: number }
  ): string =>
    current.value < range.max
      ? `${current.prefix}${current.value + 1}`
      : `${nextInvoicePrefix(current.prefix)}${range.min}`;

  // How wide a company's numeric window is when nothing above it bounds it:
  // the legacy layout's 100,000 numbers per company.
  const INVOICE_RANGE_SPAN = 100000;

  // Invoice number range per company. A configured company's upper bound is
  // the next-higher company's start, and the highest-start company gets a
  // single span above its start. It must stay bounded: an unbounded top range
  // swallows every other company's numbers (CS National's 2517 would own
  // everything up to 9007199254740991, including a 69191134 typed on some
  // other carrier's load) and makes the letter rollover unreachable, since
  // `value < range.max` would always hold. Unconfigured companies fall back to
  // the legacy per-index layout. A company's starting number is its range min.
  const deriveInvoiceNumberRanges = (): Record<string, { min: number; max: number }> => {
    const numberRanges: Record<string, { min: number; max: number }> = {};

    // Legacy fallback first
    availableCompanies.forEach((company, index) => {
      const min = 1000 + (index * INVOICE_RANGE_SPAN);
      numberRanges[invoiceRangeKey(company)] = { min, max: min + INVOICE_RANGE_SPAN - 1 };
    });

    // Then apply configured starts, deriving upper bounds by adjacency
    const configured = availableCompanies
      .filter(c => c.invoiceNumberStart != null)
      .map(c => ({
        key: invoiceRangeKey(c),
        start: c.invoiceNumberStart as number,
      }))
      .sort((a, b) => a.start - b.start);
    configured.forEach((cur, i) => {
      const next = configured[i + 1];
      numberRanges[cur.key] = {
        min: cur.start,
        max: next ? next.start - 1 : cur.start + INVOICE_RANGE_SPAN - 1,
      };
    });

    return numberRanges;
  };

  // Generate next invoice number with company-specific starting numbers.
  // When a company's range fills up the numbering does not spill into the next
  // company's range: it restarts at the bottom of the same range under a new
  // letter prefix (4999 -> A1000 -> ... -> A4999 -> B1000).
  const generateNextInvoiceNumber = async (companyCode: string = Object.keys(companyInfo)[0]): Promise<string> => {
    try {
      const numberRanges = deriveInvoiceNumberRanges();
      const range = numberRanges[companyCode];

      // Without a range we can't tell which numbers are already taken, and a
      // guess would land inside some other company's range. Refuse instead.
      if (!range) {
        throw new Error(
          `No invoice number range for company "${companyCode}" (known: ${Object.keys(numberRanges).join(', ') || 'none'})`
        );
      }

      // Carrier attribution below needs the company list; without it every row
      // looks unattributable and we would confidently hand out range.min, a
      // number this company has almost certainly already used.
      if (companies.length === 0) {
        throw new Error('Companies are still loading - cannot tell which invoice numbers belong to this carrier yet');
      }

      // loads.invoice_number is a TEXT column, so a gte/lte range filter in
      // PostgREST compares lexicographically: '10000' sits between '1000' and
      // '4999' and would be read as belonging to the 1000-4999 company. Pull
      // the non-null values and do the numeric comparison in JS instead.
      // fetchAllRows pages past the silent 1000-row PostgREST cap - with a bare
      // select, a tenant past 1000 invoiced loads gets the max of an arbitrary
      // slice and starts reissuing numbers that are already in use.
      const rows = await fetchAllRows<{ id: string; invoice_number: string | null; carrier: string | null }>(() =>
        supabase
          .from('loads')
          .select('id, invoice_number, carrier')
          .not('invoice_number', 'is', null)
      );

      // Candidates are this company's own numbers only. Deciding ownership by
      // numeric value alone is what let a hand-typed broker reference on
      // another carrier's load (69191134, LDS15529403) become the number this
      // company steps from. A row whose free-text carrier resolves to no
      // company is attributed to nobody. The range filter stays as well -
      // loads.carrier is typed by hand, so it is corroboration, not proof -
      // along with parseOwnInvoiceNumber dropping foreign-looking prefixes.
      const candidates = rows
        .filter(r => {
          const company = companyForCarrier(r.carrier);
          return !!company && invoiceRangeKey(company) === companyCode;
        })
        .map(r => parseOwnInvoiceNumber(r.invoice_number))
        .filter((p): p is ParsedInvoiceNumber => p !== null && p.value >= range.min && p.value <= range.max);

      console.log(`🔢 Scanned ${rows.length} invoiced loads, ${candidates.length} issued by ${companyCode} inside its range ${range.min}-${range.max}`);

      if (candidates.length > 0) {
        // reduce, not Math.max(...arr): the spread would overflow the argument
        // limit now that this scans every invoiced load rather than 1000.
        // Highest prefix generation wins first, then the highest number in it -
        // an old unprefixed 4999 must not outrank a current A1200.
        const highest = candidates.reduce((best, cur) => {
          const byPrefix = compareInvoicePrefixes(cur.prefix, best.prefix);
          if (byPrefix !== 0) return byPrefix > 0 ? cur : best;
          return cur.value > best.value ? cur : best;
        }, candidates[0]);
        const next = nextInvoiceNumberAfter(highest, range);
        console.log(`🔢 Highest ${companyCode} invoice found: ${highest.prefix}${highest.value}, next will be: ${next}`);
        return next;
      }

      // If no invoices exist for this company, start from the company's starting number
      return range.min.toString();
    } catch (error) {
      console.error('Error generating invoice number:', error);
      // No fallback: '1000' is the legacy first-company start, so returning it
      // here would hand out a number that is almost certainly already taken.
      // The caller reports the failure instead.
      throw error;
    }
  };

  // A load document as the invoice preview carries it: built either by
  // loadLoadDocuments() or by the Generate Invoice button's own query, so the
  // fields it can offer are name / file_path / mime_type / url (signed, 1h).
  type MergeableDocument = {
    name?: string | null;
    file_path?: string | null;
    mime_type?: string | null;
    url?: string | null;
    load_number?: string | null;
  };

  // Letter, in PDF points. Scanned BOLs are printed and mailed with the
  // invoice, so a US paper size is the one that matters.
  const LETTER_WIDTH = 612;
  const LETTER_HEIGHT = 792;
  const LETTER_MARGIN = 18;

  // What the file actually is. `documents.mime_type` is only a hint: older
  // upload paths left it null, and where it is absent the caller guesses from
  // the extension, so a JPEG can arrive as 'application/octet-stream' and a
  // real PDF as nothing at all. The magic bytes are the one reliable source;
  // the extension is consulted only when they match nothing we can embed.
  const sniffDocumentKind = (bytes: Uint8Array, docItem: MergeableDocument): 'pdf' | 'jpg' | 'png' | null => {
    const startsWith = (...signature: number[]) => signature.every((b, i) => bytes[i] === b);
    if (startsWith(0x25, 0x50, 0x44, 0x46)) return 'pdf';                         // '%PDF'
    if (startsWith(0xff, 0xd8, 0xff)) return 'jpg';                               // SOI + marker
    if (startsWith(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'png'; // 8-byte PNG signature

    // Inconclusive bytes: fall back to the name, then the stored path, then the
    // mime type. Not the signed URL - its query string is full of extensions.
    const extension = [docItem.name, docItem.file_path]
      .map(value => /\.([a-z0-9]+)\s*$/i.exec((value || '').trim())?.[1]?.toLowerCase() || '')
      .find(Boolean) || '';
    const mime = (docItem.mime_type || '').toLowerCase();
    if (extension === 'pdf' || mime === 'application/pdf') return 'pdf';
    if (extension === 'jpg' || extension === 'jpeg' || mime === 'image/jpeg' || mime === 'image/jpg') return 'jpg';
    if (extension === 'png' || mime === 'image/png') return 'png';
    return null;
  };

  // Raw bytes for one document. Storage by file_path first, the way
  // loadLoadDocuments() resolves documents: the preview's signed URL expires
  // after an hour, and a preview left open past that would otherwise start
  // dropping documents out of the generated invoice. The URL is the fallback
  // for rows whose path does not resolve in this bucket.
  const fetchDocumentBytes = async (docItem: MergeableDocument): Promise<Uint8Array> => {
    if (docItem.file_path) {
      const { data, error } = await supabase.storage
        .from('company-documents')
        .download(docItem.file_path);
      if (!error && data) return new Uint8Array(await data.arrayBuffer());
    }
    if (docItem.url) {
      const response = await fetch(docItem.url);
      if (!response.ok) throw new Error(`download failed with HTTP ${response.status}`);
      return new Uint8Array(await response.arrayBuffer());
    }
    throw new Error('document has neither a storage path nor a URL');
  };

  // Append one load document to the invoice PDF. PDFs keep the copyPages path.
  // Images get a page each: pdf-lib cannot copyPages an image, which is why a
  // JPEG BOL used to vanish from the download while still showing in the
  // preview (the preview renders each document on its own, as an <img>).
  // Throws when the document cannot be embedded - the caller reports it rather
  // than letting it disappear.
  const appendDocumentToInvoicePdf = async (mergedPdf: PDFDocument, docItem: MergeableDocument): Promise<void> => {
    const bytes = await fetchDocumentBytes(docItem);
    const kind = sniffDocumentKind(bytes, docItem);

    if (kind === 'pdf') {
      // ignoreEncryption: broker rate confirmations are often owner-password
      // protected, printable but flagged. Without this pdf-lib throws and the
      // RC drops off the invoice.
      const docPdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const docPages = await mergedPdf.copyPages(docPdf, docPdf.getPageIndices());
      docPages.forEach(page => mergedPdf.addPage(page));
      return;
    }

    if (kind === 'jpg' || kind === 'png') {
      const image = kind === 'jpg' ? await mergedPdf.embedJpg(bytes) : await mergedPdf.embedPng(bytes);
      const page = mergedPdf.addPage([LETTER_WIDTH, LETTER_HEIGHT]);
      // Fit inside the margins, never above 1:1 - these are scans of BOLs, and
      // upscaling a low-resolution photo only makes it harder to read.
      const scale = Math.min(
        (LETTER_WIDTH - LETTER_MARGIN * 2) / image.width,
        (LETTER_HEIGHT - LETTER_MARGIN * 2) / image.height,
        1
      );
      const width = image.width * scale;
      const height = image.height * scale;
      page.drawImage(image, {
        x: (LETTER_WIDTH - width) / 2,
        y: (LETTER_HEIGHT - height) / 2,
        width,
        height,
      });
      return;
    }

    throw new Error(`unsupported file type${docItem.mime_type ? ` (${docItem.mime_type})` : ''}`);
  };

  // Save a load document straight to disk, without opening a preview first.
  const handleDownloadLoadDocument = async (
    doc: { name?: string; file_path?: string; url?: string },
    docKey: string
  ) => {
    setDownloadingDocId(docKey);
    try {
      await downloadDocument(doc.file_path || doc.url || '', doc.name);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Download multiple invoices as ZIP
  const downloadInvoicesAsZip = async () => {
    if (selectedLoads.size === 0) {
      toast.error('Please select at least one load');
      return;
    }

    try {
      toast.info('Generating ZIP file...');
      const zip = new JSZip();
      const selectedLoadData = loads.filter(l => selectedLoads.has(l.id));
      
      // Determine company type from carrier filter
      const companyType = carrierFilter.toLowerCase().replace(/[^a-z0-9]/g, '');
      
      // Group by invoice number
      const invoiceGroups = groupLoadsByInvoice(selectedLoadData);
      
      // Generate PDF for each invoice with correct company type
      for (const [invoiceNum, invoiceLoads] of Object.entries(invoiceGroups)) {
        const pdfBlob = await generateInvoicePDF(invoiceLoads, companyType);
        const fileName = `Invoice_${invoiceNum.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        zip.file(fileName, pdfBlob);
      }

      // Generate ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Download
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoices_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('ZIP file downloaded successfully!');
    } catch (error) {
      console.error('Error generating ZIP:', error);
      toast.error('Failed to generate ZIP file');
    }
  };

  async function loadInvoicedLoads() {
    try {
      setLoading(true);

      const { data: loadsData, error } = await supabase
        .from('loads')
        .select(`
          *,
          dispatches!dispatches_load_id_fkey (
            driver_id,
            drivers!dispatches_driver_id_fkey (
              first_name,
              last_name,
              company_id,
              companies!drivers_company_id_fkey (
                name
              )
            )
          ),
          customers!loads_customer_id_fkey (
            name
          ),
          load_stops!load_stops_load_id_fkey (
            date,
            stop_type
          )
        `)
        .eq('invoice_sent', true)
        .in('status', ['delivered', 'DELIVERED', 'archived', 'ARCHIVED'])
        .order('invoice_sent_at', { ascending: false });

      if (error) throw error;

      // Track which invoices are marked as returned
      const returned = new Set<string>();
      (loadsData || []).forEach(load => {
        if (load.excel_returned) {
          returned.add(load.id);
        }
      });
      setReturnedInvoices(returned);

      const enrichedLoads = (loadsData || []).map((load) => {
        const dispatch = Array.isArray(load.dispatches) ? load.dispatches[0] : load.dispatches;
        const driver = dispatch?.drivers;
        const company = Array.isArray(driver?.companies) ? driver.companies[0] : driver?.companies;
        const customer = Array.isArray(load.customers) ? load.customers[0] : load.customers;
        const shipperStop = load.load_stops?.find((s: any) => s.stop_type === 'shipper');

        return {
          id: load.id,
          load_number: load.load_number,
          company_name: company?.name || 'N/A',
          driver_name: driver ? `${driver.first_name} ${driver.last_name}` : 'N/A',
          invoice_number: load.invoice_number || 'TBD',
          pickup_date: shipperStop?.date || load.pickup_date || load.pickup_time || '',
          broker_name: customer?.name || 'N/A',
          total_rate: Number(load.total_rate) || 0,
          documents_count: 0,
          invoice_sent: load.invoice_sent || false,
          invoice_sent_at: load.invoice_sent_at || '',
          payment_status: load.payment_status || 'pending',
          billing_note: load.billing_note || '',
          carrier: load.carrier || undefined,
          trufund_sent: load.trufund_sent || false,
          trufund_sent_at: load.trufund_sent_at || '',
          trufund_batch_num: load.trufund_batch_num || '',
          trufund_trans_key: load.trufund_trans_key || '',
        };
      });

      setInvoicedLoads(enrichedLoads);
    } catch (error) {
      console.error('Error loading invoiced loads:', error);
      toast.error('Failed to load invoiced loads');
    } finally {
      setLoading(false);
    }
  }

  async function loadReadyLoads() {
    try {
      setLoading(true);

      const { data: loadsData, error } = await supabase
        .from('loads')
        .select(`
          *,
          dispatches!dispatches_load_id_fkey (
            driver_id,
            drivers!dispatches_driver_id_fkey (
              first_name,
              last_name,
              company_id,
              companies!drivers_company_id_fkey (
                name
              )
            )
          ),
          customers!loads_customer_id_fkey (
            name
          ),
          load_stops!load_stops_load_id_fkey (
            date,
            stop_type
          )
        `)
        .eq('ready_for_billing', true)
        .eq('invoice_sent', false)
        .in('status', ['delivered', 'DELIVERED', 'archived', 'ARCHIVED'])
        .order('billing_sent_at', { ascending: false });

      if (error) throw error;

      const enrichedLoads = (loadsData || []).map((load) => {
        const dispatch = Array.isArray(load.dispatches) ? load.dispatches[0] : load.dispatches;
        const driver = dispatch?.drivers;
        const company = Array.isArray(driver?.companies) ? driver.companies[0] : driver?.companies;
        const customer = Array.isArray(load.customers) ? load.customers[0] : load.customers;
        const shipperStop = load.load_stops?.find((s: any) => s.stop_type === 'shipper');

        return {
          id: load.id,
          load_number: load.load_number,
          company_name: company?.name || 'N/A',
          driver_name: driver ? `${driver.first_name} ${driver.last_name}` : 'N/A',
          invoice_number: load.invoice_number || 'TBD',
          pickup_date: shipperStop?.date || load.pickup_date || load.pickup_time || '',
          broker_name: customer?.name || 'N/A',
          total_rate: Number(load.total_rate) || 0,
          documents_count: 0,
          invoice_sent: load.invoice_sent || false,
          payment_status: load.payment_status || 'pending',
          billing_note: load.billing_note || '',
          is_returned: load.is_returned || false,
          carrier: load.carrier || undefined,
        };
      });

      setLoads(enrichedLoads);
    } catch (error) {
      console.error('Error loading ready loads:', error);
      toast.error('Failed to load ready for billing loads');
    } finally {
      setLoading(false);
    }
  }

  function handleViewLoad(loadId: string) {
    window.location.href = `/loads/${loadId}`;
  }

  async function handleUpdateNote(loadId: string, note: string) {
    try {
      const { error } = await supabase
        .from('loads')
        .update({ billing_note: note })
        .eq('id', loadId);

      if (error) throw error;

      // Update local state
      setLoads(loads.map(l => l.id === loadId ? { ...l, billing_note: note } : l));
      setInvoicedLoads(invoicedLoads.map(l => l.id === loadId ? { ...l, billing_note: note } : l));
      
      toast.success('Note saved');
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to save note');
    }
  }

  async function handleUpdatePaymentStatus(loadId: string, status: 'pending' | 'billed' | 'paid') {
    try {
      const { error } = await supabase
        .from('loads')
        .update({ payment_status: status })
        .eq('id', loadId);

      if (error) throw error;

      toast.success(`Status updated to ${status}`);
      
      // Refresh both tabs
      loadReadyLoads();
      if (activeTab === 'invoices') {
        loadInvoicedLoads();
      }
    } catch (error: any) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update status');
    }
  }

  async function handleReturnToReadyForBilling(loadId: string) {
    try {
      const { error } = await supabase
        .from('loads')
        .update({ 
          invoice_sent: false,
          invoice_sent_at: null,
          payment_status: 'pending',
          is_returned: true
        })
        .eq('id', loadId);

      if (error) throw error;

      toast.success('Load returned to Ready for Billing');
      
      // Refresh both tabs
      loadReadyLoads();
      loadInvoicedLoads();
    } catch (error: any) {
      console.error('Error returning load:', error);
      toast.error('Failed to return load');
    }
  }

  // The Documents cell in the Ready table has always called handleViewDocuments,
  // but the function never existed — clicking it threw a ReferenceError, so this
  // modal was unreachable. The rows the modal renders need name / file_type /
  // mime_type / file_path, which live on `documents`, plus a signed URL for the
  // inline preview; `load_documents` alone carries none of that.
  async function loadLoadDocuments(loadId: string) {
    const { data, error } = await supabase
      .from('load_documents')
      .select('id, document_type, document_name, file_url, documents ( id, name, file_path, file_type, mime_type )')
      .eq('load_id', loadId);

    if (error) throw error;

    type LinkedDocument = {
      name?: string | null;
      file_path?: string | null;
      file_type?: string | null;
      mime_type?: string | null;
    };
    type LoadDocumentRow = {
      id: string;
      document_type?: string | null;
      document_name?: string | null;
      file_url?: string | null;
      documents?: LinkedDocument | LinkedDocument[] | null;
    };

    return Promise.all((data || []).map(async (row: LoadDocumentRow) => {
      const linked = Array.isArray(row.documents) ? row.documents[0] : row.documents;
      // Older rows link only through load_documents.file_url, which holds the
      // storage path rather than a URL.
      const filePath = linked?.file_path || row.file_url || null;

      let url: string | null = null;
      if (filePath) {
        const { data: signed } = await supabase.storage
          .from('company-documents')
          .createSignedUrl(filePath, 3600);
        url = signed?.signedUrl || null;
      }

      return {
        id: row.id,
        name: linked?.name || row.document_name || 'Untitled Document',
        file_type: linked?.file_type || null,
        mime_type: linked?.mime_type || null,
        document_type: row.document_type || null,
        file_path: filePath,
        url,
      };
    }));
  }

  async function handleViewDocuments(loadId: string) {
    try {
      setDocumentModal({ loadId, documents: await loadLoadDocuments(loadId) });
    } catch (error) {
      console.error('Error loading load documents:', error);
      toast.error('Failed to load documents');
    }
  }

  async function handleUploadDocuments() {
    if (!documentModal || uploadFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    try {
      setLoading(true);
      setUploadProgress({ current: 0, total: uploadFiles.length, label: '' });

      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        setUploadProgress({ current: i + 1, total: uploadFiles.length, label: file.name });
        const fileExt = file.name.split('.').pop()?.toLowerCase() || 'pdf';
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `load-documents/${documentModal.loadId}/${fileName}`;

        // Determine document type based on file
        let documentType = 'other';
        if (fileExt === 'pdf') {
          documentType = 'rate_confirmation';
        } else if (['jpg', 'jpeg', 'png'].includes(fileExt)) {
          documentType = 'pod';
        }

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Create document record
        const { data: docData, error: docError } = await supabase
          .from('documents')
          .insert({
            name: file.name,
            file_path: filePath,
            file_type: fileExt,
            mime_type: file.type,
            file_size: file.size,
          })
          .select()
          .single();

        if (docError) throw docError;

        // Link to load
        const { error: linkError } = await supabase
          .from('load_documents')
          .insert({
            load_id: documentModal.loadId,
            document_id: docData.id,
            document_name: file.name,
            document_type: documentType,
            file_url: filePath,
          });

        if (linkError) throw linkError;
      }

      toast.success('Documents uploaded successfully!');
      setUploadFiles([]);
      
      // Refresh documents through the same loader the modal opens with —
      // raw load_documents rows carry none of the fields it renders.
      if (documentModal?.loadId) {
        setDocumentModal({
          ...documentModal,
          documents: await loadLoadDocuments(documentModal.loadId),
        });
      }
    } catch (error: any) {
      console.error('Error uploading documents:', error);
      toast.error('Failed to upload documents');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  }

  // The carrier strip is shared by all three tabs, so tally whichever rows it is
  // currently filtering — a Ready count shown while Invoices is open would read
  // as "nothing here" and be wrong. Generated Excels filter a different shape,
  // so they get no counts.
  const carrierTallyRows =
    activeTab === 'ready' ? loads : activeTab === 'invoices' ? invoicedLoads : null;
  const carrierCountsByCompanyId = new Map<string, number>();
  let unassignedCarrierCount = 0;
  (carrierTallyRows || []).forEach((load) => {
    const company = companyForCarrier(load.carrier);
    if (company) {
      carrierCountsByCompanyId.set(company.id, (carrierCountsByCompanyId.get(company.id) || 0) + 1);
    } else {
      unassignedCarrierCount += 1;
    }
  });

  const filteredLoads = loads.filter(load => {
    // Filter by carrier
    if (!loadMatchesCarrierFilter(load)) {
      return false;
    }
    
    // Apply status filter
    if (readyStatusFilter !== 'all') {
      if (readyStatusFilter === 'ready' && load.is_returned) {
        return false;
      }
      if (readyStatusFilter === 'returned' && !load.is_returned) {
        return false;
      }
    }
    
    // Filter by search term
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      load.load_number.toLowerCase().includes(searchLower) ||
      load.driver_name.toLowerCase().includes(searchLower) ||
      load.broker_name.toLowerCase().includes(searchLower) ||
      load.company_name.toLowerCase().includes(searchLower)
    );
  });

  const totalAmount = filteredLoads.reduce((sum, l) => sum + l.total_rate, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <DollarSign className="w-7 h-7" />
                Invoices
              </h1>
              <p className="text-green-100 mt-1">
                Manage billing and invoices
              </p>
            </div>

            <div className="flex items-center gap-4">
              {activeTab === 'ready' && (
                <div className="text-right">
                  <p className="text-3xl font-bold">
                    ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-green-100">Total Amount</p>
                </div>
              )}
              <button
                onClick={async () => {
                  const draft: Record<string, string> = {};
                  availableCompanies.forEach(c => {
                    draft[c.id] = c.invoiceNumberStart != null ? String(c.invoiceNumberStart) : '';
                  });
                  setRangeDraft(draft);
                  setShowInvoiceRangeSettings(true);
                  await loadInvoiceStats();
                }}
                title="Invoice number settings"
                className="p-2 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Settings className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-t border-green-500">
          <button
            onClick={() => setActiveTab('ready')}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'ready'
                ? 'bg-white text-green-700'
                : 'text-white hover:bg-green-500'
            }`}
          >
            Ready for Billing
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'invoices'
                ? 'bg-white text-green-700'
                : 'text-white hover:bg-green-500'
            }`}
          >
            Invoices
          </button>
          <button
            onClick={() => setActiveTab('excels')}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'excels'
                ? 'bg-white text-green-700'
                : 'text-white hover:bg-green-500'
            }`}
          >
            Generated Excels
          </button>
        </div>

        {/* Carrier Filter Tabs */}
        <div className="flex border-t border-slate-200 bg-slate-50">
          {companies.map((company) => (
            <button
              key={company.id}
              onClick={() => setCarrierFilter(company.name)}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                carrierFilter === company.name
                  ? 'bg-white text-green-700 border-b-2 border-green-500'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {company.name}
              {carrierTallyRows && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 text-xs font-semibold">
                  {carrierCountsByCompanyId.get(company.id) || 0}
                </span>
              )}
            </button>
          ))}
          {/* Loads whose carrier matches no company are unreachable from every
              other tab, so give them one of their own. Nothing can be billed
              from here — there is no company to invoice under. */}
          {companies.length > 0 && (unassignedCarrierCount > 0 || isUnassignedCarrier) && (
            <button
              onClick={() => setCarrierFilter(UNASSIGNED_CARRIER)}
              title="Ready loads whose carrier doesn't match any active company"
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                isUnassignedCarrier
                  ? 'bg-white text-amber-700 border-b-2 border-amber-500'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              Unassigned
              {carrierTallyRows && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                  {unassignedCarrierCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'ready' && (
        <>
          {/* Search and Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Search by load #, driver, broker, or company..."
              />
              
              <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setReadyStatusFilter('all')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    readyStatusFilter === 'all'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setReadyStatusFilter('ready')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    readyStatusFilter === 'ready'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Ready
                </button>
                <button
                  onClick={() => setReadyStatusFilter('returned')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    readyStatusFilter === 'returned'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Returned
                </button>
              </div>
              
              <button
                onClick={async () => {
                  if (selectedLoads.size === 0) {
                    toast.error('Please select at least one load');
                    return;
                  }
                  try {
                    const selectedLoadData = filteredLoads.filter(l => selectedLoads.has(l.id));
                  
                    // Group loads by broker
                    const loadsByBroker = new Map<string, typeof selectedLoadData>();
                    selectedLoadData.forEach(load => {
                      const broker = load.broker_name || 'Unknown Broker';
                      if (!loadsByBroker.has(broker)) {
                        loadsByBroker.set(broker, []);
                      }
                      loadsByBroker.get(broker)!.push(load);
                    });

                    // Check if multiple brokers
                    if (loadsByBroker.size > 1) {
                      const brokerList = Array.from(loadsByBroker.keys()).join(', ');
                      toast.info(
                        `Selected loads have ${loadsByBroker.size} different brokers: ${brokerList}. Preview will show all loads grouped by broker. Separate invoices will be generated for each broker.`
                      );
                    }
                  
                    // Generate unique invoice numbers for each load that doesn't have one
                    const companyCode = carrierFilter.toLowerCase().replace(/[^a-z0-9]/g, '');
                    let currentInvoiceNumber = await generateNextInvoiceNumber(companyCode);
                    // The same range the number above came from, so a batch that
                    // exhausts the range part-way through rolls over to the next
                    // letter prefix instead of walking into the next company's
                    // numbers. generateNextInvoiceNumber already threw if there
                    // is no range for this company.
                    const batchRange = deriveInvoiceNumberRanges()[companyCode];
                    for (const load of selectedLoadData) {
                      // Only assign new invoice number if load doesn't have one or has 'TBD'
                      if (!load.invoice_number || load.invoice_number === 'TBD') {
                        await supabase
                          .from('loads')
                          .update({ invoice_number: currentInvoiceNumber })
                          .eq('id', load.id);
                        load.invoice_number = currentInvoiceNumber;
                        // Step with the same helper the generator uses - a plain
                        // parseInt + 1 would turn 'A1000' into NaN, and a value
                        // we can't own ('LDS…') must stop the batch, not seed it.
                        const parsed = parseOwnInvoiceNumber(currentInvoiceNumber);
                        if (!parsed) {
                          throw new Error(`Cannot continue numbering after invoice "${currentInvoiceNumber}"`);
                        }
                        currentInvoiceNumber = nextInvoiceNumberAfter(parsed, batchRange);
                      }
                    }
                  
                    // Show all loads in preview, grouped by broker
                    const firstBroker = Array.from(loadsByBroker.keys())[0];
                    const customer = firstBroker;
                    const allDocuments: any[] = [];
                    for (const load of selectedLoadData) {
                      const { data, error } = await supabase
                        .from('load_documents')
                        .select(`
                          id,
                          document_type,
                          uploaded_at,
                          document_name,
                          file_url,
                          display_order,
                          documents (
                            id,
                            name,
                            file_path,
                            file_size,
                            file_type,
                            mime_type
                          )
                        `)
                        .eq('load_id', load.id)
                        .order('display_order', { ascending: true, nullsFirst: false })
                        .order('uploaded_at', { ascending: false });

                      if (!error && data) {
                        for (const doc of data) {
                          const docData = Array.isArray(doc.documents) ? doc.documents[0] : doc.documents;
                          const filePath = docData?.file_path || doc.file_url || '';
                          if (!filePath) continue;

                          let mimeType = docData?.mime_type || 'application/octet-stream';
                          if (!docData?.mime_type && filePath) {
                            if (filePath.toLowerCase().endsWith('.pdf')) {
                              mimeType = 'application/pdf';
                            } else if (filePath.match(/\.(jpg|jpeg)$/i)) {
                              mimeType = 'image/jpeg';
                            } else if (filePath.toLowerCase().endsWith('.png')) {
                              mimeType = 'image/png';
                            }
                          }

                          const { data: signedUrlData } = await supabase.storage
                            .from('company-documents')
                            .createSignedUrl(filePath, 3600);

                          if (signedUrlData?.signedUrl) {
                            allDocuments.push({
                              id: docData?.id || doc.id,
                              name: docData?.name || doc.document_name || 'Document',
                              file_path: filePath,
                              file_type: docData?.file_type || doc.document_type,
                              mime_type: mimeType,
                              url: signedUrlData.signedUrl,
                              load_number: load.load_number,
                            });
                          }
                        }
                      }
                    }
                  
                    setInvoiceModal({ loads: selectedLoadData, customer, documents: allDocuments, companyType: companyCode, loadsByBroker });
                    setSelectedCustomerForInvoice(customer);
                    // Initialize edited amounts with current load amounts
                    const amountsMap = new Map();
                    selectedLoadData.forEach(load => amountsMap.set(load.id, load.total_rate));
                    setEditedInvoiceAmounts(amountsMap);
                  } catch (error) {
                    console.error('Error generating invoice:', error);
                    // Nothing is committed to the preview until the very last
                    // lines, so clearing the modal here just guarantees no
                    // half-built preview survives the failure.
                    setInvoiceModal(null);
                    toast.error((error as { message?: string })?.message || 'Failed to generate invoice');
                  }
                }}
                disabled={selectedLoads.size === 0 || isUnassignedCarrier}
                title={isUnassignedCarrier ? 'Assign these loads to a carrier before invoicing them' : undefined}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Generate Invoice
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedLoads.size === filteredLoads.length && filteredLoads.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLoads(new Set(filteredLoads.map(l => l.id)));
                      } else {
                        setSelectedLoads(new Set());
                      }
                    }}
                    className="rounded border-slate-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Driver</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Pick Up</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Load #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Broker</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Note</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Documents</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredLoads.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-500">
                    No loads ready for billing
                  </td>
                </tr>
              ) : (
                filteredLoads.map((load) => (
                  <tr key={load.id} className={load.is_returned ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedLoads.has(load.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedLoads);
                          if (e.target.checked) {
                            newSelected.add(load.id);
                          } else {
                            newSelected.delete(load.id);
                          }
                          setSelectedLoads(newSelected);
                        }}
                        className="rounded border-slate-300"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">
                      <div className="flex items-center gap-2">
                        {load.driver_name}
                        {load.is_returned && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-semibold">
                            Returned Load
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{load.invoice_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {load.pickup_date ? new Date(load.pickup_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold">
                      <a
                        href={`/loads/${load.id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {load.load_number}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">{load.broker_name}</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-green-600">
                      ${load.total_rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {load.billing_note ? (
                          <div className="group relative flex-1">
                            <div className="px-2 py-1 text-sm text-slate-700 bg-slate-50 rounded border border-slate-200 truncate max-w-[200px] cursor-pointer">
                              {load.billing_note}
                            </div>
                            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64 px-3 py-2 text-sm text-white bg-slate-800 rounded-lg shadow-lg">
                              {load.billing_note}
                              <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-slate-800"></div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No note</span>
                        )}
                        <button
                          onClick={() => setNoteModal({ loadId: load.id, currentNote: load.billing_note || '' })}
                          className="p-1.5 text-slate-600 hover:bg-blue-50 hover:text-blue-600 rounded transition-colors"
                          title="Edit note"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {load.payment_status === 'paid' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          Paid
                        </span>
                      ) : load.payment_status === 'billed' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                          Billed
                        </span>
                      ) : load.invoice_sent ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          Invoice Downloaded
                        </span>
                      ) : load.is_returned ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                          Returned
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                          Ready
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleViewDocuments(load.id)}>
                      <div className="flex items-center justify-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        {load.documents_count > 0 && (
                          <div className="relative">
                            <div className="absolute -top-1 -right-1 bg-slate-700 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                              +{load.documents_count}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setLoadDetailsModal(load)}
                        className="p-1.5 text-slate-600 hover:bg-slate-100 rounded"
                        title="View Load Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <>
          {/* Generate Excel Button */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-slate-600">
                {selectedInvoices.size > 0 ? `${selectedInvoices.size} invoice(s) selected` : 'No invoices selected'}
                {trufundProgress && (
                  <span className="ml-3 text-emerald-700 font-medium">
                    Sending {trufundProgress.current}/{trufundProgress.total}: {trufundProgress.label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={generateExcelExport}
                  disabled={selectedInvoices.size === 0 || trufundSending || isUnassignedCarrier}
                  title={isUnassignedCarrier ? 'Assign these loads to a carrier before exporting them' : undefined}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileText className="w-4 h-4" />
                  Generate Excel ({selectedInvoices.size})
                </button>
                {showTruFundButton && (
                  <button
                    onClick={sendSelectedToTruFund}
                    disabled={selectedInvoices.size === 0 || trufundSending}
                    className="px-6 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Build the TruFund Excel summary, attach invoice + load documents, and submit sequentially to FactorSoftWebService.FactorsoftUpload"
                  >
                    <Send className="w-4 h-4" />
                    {trufundSending ? 'Sending…' : `Send to Tru Funding (${selectedInvoices.size})`}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Invoice View Info */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                <span className="text-sm font-semibold text-slate-700">Invoices grouped by {invoicesGroupingMode === 'week' ? 'Week' : 'Month'}</span>
                <span className="text-xs text-slate-500">(Click to expand/collapse)</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setInvoicesGroupingMode('week')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                    invoicesGroupingMode === 'week'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  By Week
                </button>
                <button
                  onClick={() => setInvoicesGroupingMode('month')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                    invoicesGroupingMode === 'month'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  By Month
                </button>
                <button
                  onClick={() => {
                    const invoiceGroups = groupLoadsByInvoice(invoicedLoads);
                    setExpandedInvoices(new Set(Object.keys(invoiceGroups)));
                  }}
                  className="px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  Expand All
                </button>
                <button
                  onClick={() => setExpandedInvoices(new Set())}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded transition-colors"
                >
                  Collapse All
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by driver, invoice #, load #, broker, or note..."
                  value={invoicesSearchTerm}
                  onChange={(e) => setInvoicesSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" />
              </div>
              
              <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setInvoicesStatusFilter('all')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    invoicesStatusFilter === 'all'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setInvoicesStatusFilter('billed')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    invoicesStatusFilter === 'billed'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Billed
                </button>
                <button
                  onClick={() => setInvoicesStatusFilter('paid')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    invoicesStatusFilter === 'paid'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Paid
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.size === invoicedLoads.length && invoicedLoads.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInvoices(new Set(invoicedLoads.map(l => l.id)));
                        } else {
                          setSelectedInvoices(new Set());
                        }
                      }}
                      className="rounded border-slate-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Driver</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Invoice #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Pick Up</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Load #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Broker</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Note</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Invoice Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">PDF</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Excel</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">TruFund</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {invoicedLoads.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="px-4 py-8 text-center text-slate-500">
                      No invoices created yet
                    </td>
                  </tr>
                ) : (
                  (() => {
                    // Filter invoiced loads based on search term
                    const filteredInvoicedLoads = invoicedLoads.filter(load => {
                      // Filter by carrier
                      if (!loadMatchesCarrierFilter(load)) {
                        return false;
                      }
                      
                      // Filter by status
                      if (invoicesStatusFilter !== 'all') {
                        if (invoicesStatusFilter === 'returned' && !load.is_returned) {
                          return false;
                        }
                        if (invoicesStatusFilter === 'billed' && load.payment_status !== 'billed') {
                          return false;
                        }
                        if (invoicesStatusFilter === 'paid' && load.payment_status !== 'paid') {
                          return false;
                        }
                      }
                      
                      // Filter by search term
                      if (!invoicesSearchTerm) return true;
                      const searchLower = invoicesSearchTerm.toLowerCase();
                      return (
                        load.driver_name?.toLowerCase().includes(searchLower) ||
                        load.invoice_number?.toLowerCase().includes(searchLower) ||
                        load.load_number?.toLowerCase().includes(searchLower) ||
                        load.broker_name?.toLowerCase().includes(searchLower) ||
                        load.billing_note?.toLowerCase().includes(searchLower)
                      );
                    });

                    // Group by week or month first
                    const groupedByPeriod: Record<string, ReadyLoad[]> = {};
                    filteredInvoicedLoads.forEach(load => {
                      const pickupDate = load.pickup_date ? new Date(load.pickup_date) : new Date();
                      const periodStart = invoicesGroupingMode === 'week' ? getWeekStart(pickupDate) : getMonthStart(pickupDate);
                      const key = periodStart.toISOString();
                      if (!groupedByPeriod[key]) {
                        groupedByPeriod[key] = [];
                      }
                      groupedByPeriod[key].push(load);
                    });

                    // Sort periods (most recent first)
                    const sortedPeriods = Object.keys(groupedByPeriod).sort((a, b) => 
                      new Date(b).getTime() - new Date(a).getTime()
                    );

                    const rows: JSX.Element[] = [];
                    
                    // Iterate through each period
                    sortedPeriods.forEach(periodKey => {
                      const periodLoads = groupedByPeriod[periodKey];
                      const isPeriodExpanded = expandedInvoices.has(periodKey);
                      const periodLabel = invoicesGroupingMode === 'week' 
                        ? formatWeekRange(periodKey)
                        : formatMonthRange(periodKey);
                      const periodTotal = periodLoads.reduce((sum, l) => sum + l.total_rate, 0);

                      // Period header row
                      rows.push(
                        <tr key={`period-${periodKey}`} className="bg-gradient-to-r from-blue-50 to-slate-50 hover:from-blue-100 hover:to-slate-100 transition-all duration-200 border-b-2 border-blue-200">
                          <td colSpan={13} className="px-6 py-4 cursor-pointer" onClick={() => {
                            const newExpanded = new Set(expandedInvoices);
                            if (isPeriodExpanded) {
                              newExpanded.delete(periodKey);
                            } else {
                              newExpanded.add(periodKey);
                            }
                            setExpandedInvoices(newExpanded);
                          }}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {isPeriodExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-blue-600" />
                                )}
                                <Calendar className="w-5 h-5 text-blue-600" />
                                <span className="text-lg font-bold text-slate-900">{periodLabel}</span>
                                <span className="ml-3 px-3 py-1 bg-blue-600 text-white text-xs rounded-full font-semibold shadow-sm">
                                  {periodLoads.length} {periodLoads.length === 1 ? 'invoice' : 'invoices'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">Total:</span>
                                <span className="text-xl font-bold text-green-700">
                                  ${periodTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );

                      // Only show invoices if period is expanded
                      if (!isPeriodExpanded) return;

                      const invoiceGroups = groupLoadsByInvoice(periodLoads);
                      Object.keys(invoiceGroups).sort().forEach(invoiceNumber => {
                      const loads = invoiceGroups[invoiceNumber];
                      // Sort loads by invoice number (smallest to largest)
                      loads.sort((a, b) => {
                        const numA = parseInt(a.invoice_number) || 0;
                        const numB = parseInt(b.invoice_number) || 0;
                        return numA - numB;
                      });
                      const isExpanded = expandedInvoices.has(invoiceNumber);
                      const totalAmount = loads.reduce((sum, l) => sum + l.total_rate, 0);
                      const allSelected = loads.every(l => selectedInvoices.has(l.id));
                      const someSelected = loads.some(l => selectedInvoices.has(l.id));
                      
                      // Get the invoice date from the first load
                      const invoiceDate = loads[0]?.invoice_sent_at 
                        ? new Date(loads[0].invoice_sent_at).toLocaleDateString() 
                        : 'N/A';
                      
                      // Determine group color based on payment status
                      const allPaid = loads.every(l => l.payment_status === 'paid');
                      const allBilled = loads.every(l => l.payment_status === 'billed');
                      const groupHeaderColor = allPaid 
                        ? 'bg-green-100 hover:bg-green-200 border-green-300' 
                        : allBilled 
                        ? 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300' 
                        : 'bg-blue-50 hover:bg-blue-100 border-blue-200';
                      
                      // Check if any load in this group has been included in Excel
                      const hasExcelGenerated = loads.some(l => invoicesInExcel.has(l.id));
                      
                      // Invoice header row
                      rows.push(
                        <tr key={`invoice-${invoiceNumber}`} className={`${groupHeaderColor} font-semibold border-b-2`}>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={(input) => {
                                if (input) input.indeterminate = someSelected && !allSelected;
                              }}
                              onChange={(e) => {
                                const newSelected = new Set(selectedInvoices);
                                if (e.target.checked) {
                                  loads.forEach(l => newSelected.add(l.id));
                                } else {
                                  loads.forEach(l => newSelected.delete(l.id));
                                }
                                setSelectedInvoices(newSelected);
                              }}
                              className="rounded border-slate-300"
                            />
                          </td>
                          <td colSpan={6} className="px-4 py-3 cursor-pointer" onClick={() => toggleInvoice(invoiceNumber)}>
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-blue-600" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-blue-600" />
                              )}
                              <FileText className="w-5 h-5 text-blue-600" />
                              <span className="text-slate-900 text-base">Invoice Date: {invoiceDate}</span>
                              <span className="ml-2 px-2 py-0.5 bg-blue-200 text-blue-900 text-xs rounded-full font-bold">
                                {loads.length} {loads.length === 1 ? 'load' : 'loads'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-blue-700">
                            ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-center"></td>
                          <td className="px-4 py-3 text-center text-xs font-semibold">
                            <span className={hasExcelGenerated ? 'text-green-600' : 'text-slate-400'}>
                              {hasExcelGenerated ? 'Generated' : 'Not Generated'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-xs font-semibold">
                            {(() => {
                              const allTruFundSent = loads.every((l) => l.trufund_sent);
                              const someTruFundSent = loads.some((l) => l.trufund_sent);
                              if (allTruFundSent) return <span className="text-emerald-700">Sent</span>;
                              if (someTruFundSent) return <span className="text-emerald-500">Partial</span>;
                              return <span className="text-slate-400">—</span>;
                            })()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {allPaid ? (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const updatePromises = loads.map(load => 
                                      supabase
                                        .from('loads')
                                        .update({ payment_status: 'billed' })
                                        .eq('id', load.id)
                                    );
                                    await Promise.all(updatePromises);
                                    toast.success('All loads marked as billed');
                                    loadInvoicedLoads();
                                  } catch (error) {
                                    console.error('Error marking as billed:', error);
                                    toast.error('Failed to mark as billed');
                                  }
                                }}
                                className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors font-semibold"
                                title="Mark all loads as billed"
                              >
                                Mark as Billed
                              </button>
                            ) : (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const updatePromises = loads.map(load => 
                                      supabase
                                        .from('loads')
                                        .update({ payment_status: 'paid' })
                                        .eq('id', load.id)
                                    );
                                    await Promise.all(updatePromises);
                                    toast.success('All loads marked as paid');
                                    loadInvoicedLoads();
                                  } catch (error) {
                                    console.error('Error marking as paid:', error);
                                    toast.error('Failed to mark as paid');
                                  }
                                }}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors font-semibold"
                                title="Mark all loads as paid"
                              >
                                Mark as Paid
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                      
                      // Individual load rows (only if expanded)
                      if (isExpanded) {
                        loads.forEach((load) => {
                            // Mark returned invoices in red
                            const isReturned = returnedInvoices.has(load.id);
                            const rowColor = isReturned ? 'bg-red-50 hover:bg-red-100' : 'bg-white hover:bg-slate-50';
                            
                            rows.push(
                              <tr key={load.id} className={rowColor}>
                                <td className="px-4 py-3 pl-12">
                                  <input
                                    type="checkbox"
                                    checked={selectedInvoices.has(load.id)}
                                    onChange={(e) => {
                                      const newSelected = new Set(selectedInvoices);
                                      if (e.target.checked) {
                                        newSelected.add(load.id);
                                      } else {
                                        newSelected.delete(load.id);
                                      }
                                      setSelectedInvoices(newSelected);
                                    }}
                                    className="rounded border-slate-300"
                                  />
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-900">{load.driver_name}</td>
                                <td className="px-4 py-3 text-sm text-slate-600 font-semibold">{load.invoice_number}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  {load.pickup_date ? new Date(load.pickup_date).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold">
                                  <a
                                    href={`/loads/${load.id}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {load.load_number}
                                  </a>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-900">{load.broker_name}</td>
                                <td className="px-4 py-3 text-right text-sm font-bold text-green-600">
                                  ${load.total_rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    {load.billing_note ? (
                                      <div className="group relative flex-1">
                                        <div className="px-2 py-1 text-sm text-slate-700 bg-slate-50 rounded border border-slate-200 truncate max-w-[200px] cursor-pointer">
                                          {load.billing_note}
                                        </div>
                                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-64 px-3 py-2 text-sm text-white bg-slate-800 rounded-lg shadow-lg">
                                          {load.billing_note}
                                          <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-slate-800"></div>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-400 italic">No note</span>
                                    )}
                                    <button
                                      onClick={() => setNoteModal({ loadId: load.id, currentNote: load.billing_note || '' })}
                                      className="p-1.5 text-slate-600 hover:bg-blue-50 hover:text-blue-600 rounded transition-colors"
                                      title="Edit note"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center text-sm text-slate-600">
                                  {load.invoice_sent_at ? new Date(load.invoice_sent_at).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {(() => {
                                    const pdfRecord = generatedInvoicePdfs.find(pdf => pdf.load_id === load.id);
                                    return pdfRecord ? (
                                      <button
                                        onClick={async () => {
                                          try {
                                            const blob = await fetchInvoicePdfBlob(pdfRecord);
                                            const url = window.URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `${pdfRecord.invoice_number}.pdf`;
                                            document.body.appendChild(a);
                                            a.click();
                                            document.body.removeChild(a);
                                            window.URL.revokeObjectURL(url);
                                          } catch (error) {
                                            console.error('Error downloading PDF:', error);
                                            toast.error('Failed to download PDF');
                                          }
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors font-semibold"
                                        title="Download Invoice PDF"
                                      >
                                        <Download className="w-3 h-3" />
                                        PDF
                                      </button>
                                    ) : (
                                      <span className="text-slate-300 text-xs">-</span>
                                    );
                                  })()}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {invoicesInExcel.has(load.id) ? (
                                    <div className="flex items-center justify-center" title="Included in Excel">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                        <polyline points="10 9 9 9 8 9"></polyline>
                                      </svg>
                                    </div>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center text-xs">
                                  {load.trufund_sent ? (
                                    <div
                                      className="text-emerald-700 font-semibold"
                                      title={`Sent ${load.trufund_sent_at ? new Date(load.trufund_sent_at).toLocaleString() : ''}${load.trufund_batch_num ? ` • Batch ${load.trufund_batch_num}` : ''}${load.trufund_trans_key ? ` • Trans ${load.trufund_trans_key}` : ''}`}
                                    >
                                      Sent
                                      {load.trufund_batch_num && (
                                        <div className="text-[10px] text-slate-500">#{load.trufund_batch_num}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <select
                                      value={load.payment_status}
                                      onChange={(e) => handleUpdatePaymentStatus(load.id, e.target.value as 'pending' | 'billed' | 'paid')}
                                      className="px-3 py-1.5 border border-slate-300 rounded text-xs font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="billed">Billed</option>
                                      <option value="paid">Paid</option>
                                    </select>
                                    <button
                                      onClick={() => handleReturnToReadyForBilling(load.id)}
                                      className="px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors font-semibold"
                                      title="Return to Ready for Billing"
                                    >
                                      Return Load
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                        });
                      }
                      });
                    });
                    
                    return rows;
                  })()
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Generated Excels Tab */}
      {activeTab === 'excels' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Generated Excel Files</h3>
                <p className="text-sm text-slate-600 mt-1">History of all generated Excel exports</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setExcelsGroupingMode('week')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                    excelsGroupingMode === 'week'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  By Week
                </button>
                <button
                  onClick={() => setExcelsGroupingMode('month')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                    excelsGroupingMode === 'month'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  By Month
                </button>
              </div>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search by filename or date..."
                value={excelsSearchTerm}
                onChange={(e) => setExcelsSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" />
            </div>
          </div>
          
          {generatedExcels.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Excel Files Generated Yet</h3>
              <p className="text-slate-500">Go to the Invoices tab and select invoices to generate an Excel file</p>
            </div>
          ) : (
            <div className="space-y-4 p-6">
              {(() => {
                // Filter excels based on search term and carrier
                const filteredExcels = generatedExcels.filter(excel => {
                  // Filter by carrier - use the carrier column directly
                  if (excel.carrier && excel.carrier !== carrierFilter) {
                    return false;
                  }
                  
                  // Fallback: If carrier column doesn't exist (old records), check invoices
                  if (!excel.carrier) {
                    const hasMatchingCarrier = excel.invoice_ids.some(loadId => {
                      const load = invoicedLoads.find(l => l.id === loadId);
                      return load?.carrier === carrierFilter;
                    });
                    if (!hasMatchingCarrier) return false;
                  }
                  
                  // Filter by search term
                  if (!excelsSearchTerm) return true;
                  const searchLower = excelsSearchTerm.toLowerCase();
                  const dateString = new Date(excel.generated_date).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }).toLowerCase();
                  return (
                    excel.filename.toLowerCase().includes(searchLower) ||
                    dateString.includes(searchLower)
                  );
                });

                // Group excels by week or month
                const grouped: Record<string, GeneratedExcel[]> = {};
                filteredExcels.forEach(excel => {
                  const date = new Date(excel.generated_date);
                  let key: string;
                  
                  if (excelsGroupingMode === 'week') {
                    const weekStart = getWeekStart(date);
                    key = weekStart.toISOString();
                  } else {
                    const monthStart = getMonthStart(date);
                    key = monthStart.toISOString();
                  }
                  
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(excel);
                });

                return Object.keys(grouped).sort((a, b) => {
                  const dateA = new Date(grouped[a][0].generated_date);
                  const dateB = new Date(grouped[b][0].generated_date);
                  return dateB.getTime() - dateA.getTime();
                }).map(periodKey => {
                  const isExpanded = expandedExcelGroups.has(periodKey);
                  const periodExcels = grouped[periodKey];
                  // Calculate period total based on current returned status
                  const periodTotal = periodExcels.reduce((sum, excel) => {
                    let excelTotal = excel.total_amount;
                    excel.invoice_ids.forEach(loadId => {
                      if (returnedInvoices.has(loadId)) {
                        const load = invoicedLoads.find(l => l.id === loadId);
                        if (load) {
                          excelTotal -= load.total_rate;
                        }
                      }
                    });
                    return sum + excelTotal;
                  }, 0);
                  const periodLabel = excelsGroupingMode === 'week' 
                    ? formatWeekRange(periodKey)
                    : formatMonthRange(periodKey);
                  
                  return (
                  <div key={periodKey} className="border border-slate-200 rounded-lg overflow-hidden">
                    <div 
                      className="bg-slate-100 px-4 py-2 border-b border-slate-200 cursor-pointer hover:bg-slate-200 transition-colors"
                      onClick={() => {
                        const newExpanded = new Set(expandedExcelGroups);
                        if (isExpanded) {
                          newExpanded.delete(periodKey);
                        } else {
                          newExpanded.add(periodKey);
                        }
                        setExpandedExcelGroups(newExpanded);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-slate-600" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-600" />
                          )}
                          <Calendar className="w-5 h-5 text-slate-600" />
                          <h4 className="font-semibold text-slate-900">{periodLabel}</h4>
                          <span className="ml-2 px-2 py-0.5 bg-slate-300 text-slate-900 text-xs rounded-full font-bold">
                            {periodExcels.length} {periodExcels.length === 1 ? 'file' : 'files'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-semibold text-slate-900">
                            ${periodTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Filename</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Generated Date</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Invoice Count</th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total Amount</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {grouped[periodKey].map((excel) => (
                    <tr key={excel.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-slate-900">{excel.filename}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(excel.generated_date).toLocaleString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {excel.invoice_count} {excel.invoice_count === 1 ? 'invoice' : 'invoices'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                        ${(() => {
                          // Start with original total and subtract returned invoices
                          let currentTotal = excel.total_amount;
                          excel.invoice_ids.forEach(loadId => {
                            if (returnedInvoices.has(loadId)) {
                              const load = invoicedLoads.find(l => l.id === loadId);
                              if (load) {
                                currentTotal -= load.total_rate;
                              }
                            }
                          });
                          return currentTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={async () => {
                              try {
                                // Fetch load data from database using stored invoice_ids
                                const { data: loadsData, error } = await supabase
                                  .from('loads')
                                  .select(`
                                    id,
                                    load_number,
                                    invoice_number,
                                    total_rate,
                                    customers!loads_customer_id_fkey (
                                      name
                                    )
                                  `)
                                  .in('id', excel.invoice_ids);

                                if (error) throw error;

                                if (!loadsData || loadsData.length === 0) {
                                  toast.error('Invoice data no longer available');
                                  return;
                                }

                                // Map the data
                                const loadsToExport = loadsData.map(load => {
                                  const customer = Array.isArray(load.customers) ? load.customers[0] : load.customers;
                                  return {
                                    id: load.id,
                                    broker_name: customer?.name || '',
                                    invoice_number: load.invoice_number || '',
                                    load_number: load.load_number || '',
                                    total_rate: load.total_rate || 0
                                  };
                                });

                                // Sort by invoice number (smallest to largest)
                                loadsToExport.sort((a, b) => {
                                  const numA = parseInt(a.invoice_number) || 0;
                                  const numB = parseInt(b.invoice_number) || 0;
                                  return numA - numB;
                                });

                                const exportData = loadsToExport.map(load => ({
                                  'DTR_NAME': load.broker_name || '',
                                  'INVOICE#': load.invoice_number || '',
                                  'INV_DATE': new Date(excel.generated_date).toLocaleDateString('en-US'),
                                  'PO': load.load_number || '',
                                  'INVAMT': load.total_rate || 0
                                }));

                                setExcelPreviewModal({ filename: excel.filename, data: exportData });
                              } catch (error) {
                                console.error('Error loading Excel preview:', error);
                                toast.error('Failed to load Excel preview');
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded transition-colors flex items-center gap-1"
                            title="Preview Excel data"
                          >
                            <Eye className="w-4 h-4" />
                            Preview
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                // Fetch load data from database using stored invoice_ids
                                const { data: loadsData, error } = await supabase
                                  .from('loads')
                                  .select(`
                                    id,
                                    load_number,
                                    invoice_number,
                                    total_rate,
                                    customers!loads_customer_id_fkey (
                                      name
                                    )
                                  `)
                                  .in('id', excel.invoice_ids);

                                if (error) throw error;

                                if (!loadsData || loadsData.length === 0) {
                                  toast.error('Invoice data no longer available');
                                  return;
                                }

                                // Map the data
                                const loadsToExport = loadsData.map(load => {
                                  const customer = Array.isArray(load.customers) ? load.customers[0] : load.customers;
                                  return {
                                    broker_name: customer?.name || '',
                                    invoice_number: load.invoice_number || '',
                                    load_number: load.load_number || '',
                                    total_rate: load.total_rate || 0
                                  };
                                });

                                // Sort by invoice number (smallest to largest)
                                loadsToExport.sort((a, b) => {
                                  const numA = parseInt(a.invoice_number) || 0;
                                  const numB = parseInt(b.invoice_number) || 0;
                                  return numA - numB;
                                });

                                // Calculate total amount
                                const totalAmount = loadsToExport.reduce((sum, load) => sum + load.total_rate, 0);

                                // Create workbook and worksheet using ExcelJS
                                const workbook = new ExcelJS.Workbook();
                                const worksheet = workbook.addWorksheet('Invoices');

                                // Set column widths
                                worksheet.columns = [
                                  { width: 35 }, // DTR_NAME
                                  { width: 25 }, // INVOICE#
                                  { width: 12 }, // INV_DATE
                                  { width: 20 }, // PO
                                  { width: 12 }  // INVAMT
                                ];

                                // Row 1: Headers
                                const headerRow = worksheet.getRow(1);
                                headerRow.values = ['DTR_NAME', 'INVOICE#', 'INV_DATE', 'PO', 'INVAMT'];
                                headerRow.font = { bold: true };
                                headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
                                headerRow.eachCell((cell) => {
                                  cell.border = {
                                    top: { style: 'thin' },
                                    left: { style: 'thin' },
                                    bottom: { style: 'thin' },
                                    right: { style: 'thin' }
                                  };
                                });

                                // Row 2+: Data rows
                                let currentRow = 2;
                                loadsToExport.forEach(load => {
                                  const row = worksheet.getRow(currentRow);
                                  row.values = [
                                    load.broker_name || '',
                                    load.invoice_number || '',
                                    new Date(excel.generated_date).toLocaleDateString('en-US'),
                                    load.load_number || '',
                                    load.total_rate || 0
                                  ];
                                  row.eachCell((cell, colNumber) => {
                                    cell.border = {
                                      top: { style: 'thin' },
                                      left: { style: 'thin' },
                                      bottom: { style: 'thin' },
                                      right: { style: 'thin' }
                                    };
                                    cell.alignment = { vertical: 'middle' };
                                    if (colNumber === 5) {
                                      cell.alignment = { horizontal: 'right', vertical: 'middle' };
                                    }
                                  });
                                  currentRow++;
                                });

                                // Add empty rows to reach at least 25 data rows
                                const minRows = 25;
                                while (currentRow <= minRows + 1) {
                                  const row = worksheet.getRow(currentRow);
                                  row.values = ['', '', '', '', ''];
                                  row.eachCell((cell) => {
                                    cell.border = {
                                      top: { style: 'thin' },
                                      left: { style: 'thin' },
                                      bottom: { style: 'thin' },
                                      right: { style: 'thin' }
                                    };
                                  });
                                  currentRow++;
                                }

                                // Add summary row
                                const summaryRow = worksheet.getRow(currentRow);
                                summaryRow.values = [loadsToExport.length, 'INVOICES - THIS SCHEDULE:', '', '', totalAmount];
                                summaryRow.getCell(1).font = { bold: true };
                                summaryRow.getCell(1).alignment = { horizontal: 'center' };
                                summaryRow.getCell(2).font = { bold: true };
                                summaryRow.getCell(2).alignment = { horizontal: 'center' };
                                summaryRow.getCell(5).font = { bold: true };
                                summaryRow.getCell(5).alignment = { horizontal: 'right' };
                                summaryRow.getCell(5).border = {
                                  top: { style: 'thin' },
                                  left: { style: 'thin' },
                                  bottom: { style: 'thin' },
                                  right: { style: 'thin' }
                                };
                                currentRow++;

                                // Add empty row
                                currentRow++;

                                // Add legal text
                                const legalText = "For valuable consideration, receipt of which is hereby acknowledged, the undersigned hereby SELLS, assigns, sets over and transfers to TBK Bank, SSB d/b/a Triumph (\"TRIUMPH\"), its successors or assigns, all its rights, title and interest in and to the invoices listed above, including all monies due or to become due thereon, all in accordance with and pursuant to the Factoring and Security Agreement now existing by and between the undersigned and TRIUMPH, the conditions, representations, warranties and agreements of which are made part of this SALE and assignment and incorporated herein by reference.";
                                const legalRow = worksheet.getRow(currentRow);
                                legalRow.getCell(1).value = legalText;
                                legalRow.getCell(1).font = { size: 9 };
                                legalRow.getCell(1).alignment = { wrapText: true, vertical: 'top' };
                                worksheet.mergeCells(currentRow, 1, currentRow, 5);
                                legalRow.height = 60;
                                currentRow++;

                                // Add company name label
                                const companyLabelRow = worksheet.getRow(currentRow);
                                companyLabelRow.getCell(1).value = 'Company Name';
                                companyLabelRow.getCell(1).font = { bold: true };
                                currentRow++;

                                // Add company name input box
                                const companyInputRow = worksheet.getRow(currentRow);
                                companyInputRow.getCell(1).value = '';
                                worksheet.mergeCells(currentRow, 1, currentRow, 3);
                                companyInputRow.getCell(1).border = {
                                  top: { style: 'thin' },
                                  left: { style: 'thin' },
                                  bottom: { style: 'thin' },
                                  right: { style: 'thin' }
                                };
                                currentRow++;

                                // Add authorization signature label
                                const sigLabelRow = worksheet.getRow(currentRow);
                                sigLabelRow.getCell(1).value = 'Authorization Signature';
                                sigLabelRow.getCell(1).font = { bold: true };
                                currentRow++;

                                // Add signature input boxes
                                const sigInputRow = worksheet.getRow(currentRow);
                                sigInputRow.getCell(1).value = '';
                                sigInputRow.getCell(4).value = 'Date of Assignment';
                                sigInputRow.getCell(4).font = { bold: true };
                                worksheet.mergeCells(currentRow, 1, currentRow, 3);
                                worksheet.mergeCells(currentRow, 4, currentRow, 5);
                                sigInputRow.getCell(1).border = {
                                  top: { style: 'thin' },
                                  left: { style: 'thin' },
                                  bottom: { style: 'thin' },
                                  right: { style: 'thin' }
                                };
                                sigInputRow.getCell(4).border = {
                                  top: { style: 'thin' },
                                  left: { style: 'thin' },
                                  bottom: { style: 'thin' },
                                  right: { style: 'thin' }
                                };

                                // Save file
                                const buffer = await workbook.xlsx.writeBuffer();
                                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = excel.filename;
                                link.click();
                                window.URL.revokeObjectURL(url);

                                toast.success(`Downloaded: ${excel.filename}`);
                              } catch (error) {
                                console.error('Error downloading Excel:', error);
                                toast.error('Failed to download Excel file');
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-semibold text-green-600 hover:bg-green-50 rounded transition-colors flex items-center gap-1"
                            title="Download Excel file"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete "${excel.filename}"? This will only remove it from the list, not from your downloads.`)) {
                                try {
                                  // Delete from database
                                  const { error } = await supabase
                                    .from('generated_excels')
                                    .delete()
                                    .eq('id', excel.id);

                                  if (error) throw error;

                                  // Remove from local state
                                  setGeneratedExcels(prev => prev.filter(e => e.id !== excel.id));
                                  
                                  // Remove invoice IDs from the "in Excel" set
                                  const newInvoicesInExcel = new Set(invoicesInExcel);
                                  excel.invoice_ids.forEach(id => newInvoicesInExcel.delete(id));
                                  setInvoicesInExcel(newInvoicesInExcel);
                                  
                                  toast.success(`Deleted: ${excel.filename}`);
                                } catch (error) {
                                  console.error('Error deleting Excel record:', error);
                                  toast.error('Failed to delete Excel record');
                                }
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 rounded transition-colors flex items-center gap-1"
                            title="Delete Excel record"
                          >
                            <X className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}


      {/* Invoice Modal */}
      {invoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setInvoiceModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Invoice Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      toast.info('Generating separate PDFs for each load...');
                      
                      const selectedCompany = invoiceModal.companyType ? companyInfo[invoiceModal.companyType] : Object.values(companyInfo)[0];

                      // Update customer for each load if changed
                      for (const load of invoiceModal.loads) {
                        const selectedCustomerName = selectedCustomersPerLoad.get(load.id);
                        if (selectedCustomerName && selectedCustomerName !== load.broker_name) {
                          const selectedCustomer = allCustomers.find(c => c.name === selectedCustomerName);
                          if (selectedCustomer) {
                            await supabase
                              .from('loads')
                              .update({ 
                                customer_id: selectedCustomer.id,
                                customer_name: selectedCustomer.name
                              })
                              .eq('id', load.id);
                          }
                        }
                      }

                      // Block the download if any edited invoice number collides with another load.
                      const hasConflict = invoiceModal.loads.some(l => invoiceNumberConflicts.get(l.id) === 'conflict');
                      if (hasConflict) {
                        toast.error('Fix the highlighted duplicate invoice number before downloading.');
                        return;
                      }

                      // Update amounts if changed
                      for (const load of invoiceModal.loads) {
                        const editedAmount = editedInvoiceAmounts.get(load.id);
                        if (editedAmount !== undefined && editedAmount !== load.total_rate) {
                          // Update load total_rate
                          await supabase
                            .from('loads')
                            .update({ total_rate: editedAmount })
                            .eq('id', load.id);

                          // Update dispatch driver_pay if exists
                          await supabase
                            .from('dispatches')
                            .update({ driver_pay: editedAmount })
                            .eq('load_id', load.id);
                        }
                      }

                      // Persist edited invoice numbers
                      for (const load of invoiceModal.loads) {
                        const editedNum = editedInvoiceNumbers.get(load.id);
                        if (editedNum !== undefined && editedNum.trim() !== '' && editedNum.trim() !== (load.invoice_number || '')) {
                          const { error: numError } = await supabase
                            .from('loads')
                            .update({ invoice_number: editedNum.trim() })
                            .eq('id', load.id);
                          if (numError) {
                            toast.error(`Failed to save invoice number for load ${load.load_number}: ${numError.message}`);
                            return;
                          }
                          // Reflect locally so the current PDF render uses the new number.
                          load.invoice_number = editedNum.trim();
                        }
                      }

                      // Documents that could not be merged into any invoice, so
                      // the user is told instead of finding out from the broker.
                      const skippedDocuments: string[] = [];

                      // Generate separate PDF for each load
                      for (const load of invoiceModal.loads) {
                        // Get documents for this specific load
                        const loadDocuments = invoiceModal.documents.filter(doc => doc.load_number === load.load_number);

                        // Step 1: Generate invoice page for this load
                        const doc = new jsPDF();
                        let yPos = 20;

                        // PAYABLE TO - pulled from the selected company's factoring_info; skipped when empty
                        const factoringLines = (selectedCompany?.factoringInfo || '')
                          .split('\n')
                          .map(l => l.trimEnd())
                          .filter(l => l.length > 0);
                        if (factoringLines.length > 0) {
                          doc.setFontSize(10);
                          doc.setFont('helvetica', 'bold');
                          doc.text('PAYABLE TO:', 14, yPos);
                          yPos += 6;
                          doc.setFont('helvetica', 'normal');
                          doc.setFontSize(11);
                          factoringLines.forEach((line, idx) => {
                            if (idx === 0) {
                              doc.setFont('helvetica', 'bold');
                              doc.setFontSize(12);
                            } else {
                              doc.setFont('helvetica', 'normal');
                              doc.setFontSize(10);
                            }
                            doc.text(line, 14, yPos);
                            yPos += idx === 0 ? 6 : 5;
                          });
                          yPos += 10;
                        }

                        // ASSIGNED FOR
                        doc.setFontSize(10);
                        doc.setFont('helvetica', 'bold');
                        doc.text('Assigned For:', 14, yPos);
                        yPos += 6;
                        doc.setFontSize(11);
                        doc.setFont('helvetica', 'normal');
                        
                        if (selectedCompany) {
                          doc.text(selectedCompany.name, 14, yPos);
                          yPos += 5;
                          doc.setFontSize(9);
                          doc.text(selectedCompany.address, 14, yPos);
                          yPos += 4;
                          doc.text(`${selectedCompany.city}, ${selectedCompany.state} ${selectedCompany.zip}`, 14, yPos);
                          yPos += 4;
                          doc.text(selectedCompany.country, 14, yPos);
                        } else {
                          doc.text('APOLLO FREIGHT SYSTEMS INC', 14, yPos);
                          yPos += 5;
                          doc.setFontSize(9);
                          doc.text('5900 S Archer Rd Unit 128', 14, yPos);
                          yPos += 4;
                          doc.text('Summit, IL 60501', 14, yPos);
                          yPos += 4;
                          doc.text('United States', 14, yPos);
                        }
                        yPos += 15;

                        // INVOICE TITLE
                        doc.setFontSize(24);
                        doc.setFont('helvetica', 'bold');
                        doc.text('INVOICE', 105, yPos, { align: 'center' });
                        yPos += 15;

                        // BILL TO
                        doc.setFontSize(10);
                        doc.setFont('helvetica', 'bold');
                        doc.text('BILL TO:', 14, yPos);
                        yPos += 6;
                        doc.setFontSize(12);
                        const customerForThisLoad = selectedCustomersPerLoad.get(load.id) || load.broker_name || 'Unknown Broker';
                        doc.text(customerForThisLoad, 14, yPos);
                        yPos += 6;
                        doc.setFontSize(9);
                        doc.setFont('helvetica', 'normal');
                        doc.text('Rates and charges:', 14, yPos);
                        yPos += 10;

                        // INVOICE TABLE (single load) - use edited amount if changed
                        const invoiceAmount = editedInvoiceAmounts.get(load.id) ?? load.total_rate;
                        const tableData = [[
                          load.invoice_number,
                          load.load_number,
                          `$${invoiceAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        ]];

                        autoTable(doc, {
                          startY: yPos,
                          head: [['INVOICE Number', 'PO Number (reference, load)', 'Amount']],
                          body: tableData,
                          foot: [['TOTAL', '', `$${invoiceAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]],
                          theme: 'grid',
                          headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
                          footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
                        });

                        yPos = (doc as any).lastAutoTable.finalY + 20;

                        // FOOTER
                        doc.setFontSize(14);
                        doc.setFont('helvetica', 'bold');
                        doc.text('THANK YOU FOR YOUR BUSINESS!', 105, yPos, { align: 'center' });
                        
                        // Add generation date
                        yPos += 10;
                        doc.setFontSize(9);
                        doc.setFont('helvetica', 'normal');
                        const generationDate = new Date().toLocaleString('en-US', { 
                          month: '2-digit', 
                          day: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                        doc.text(`Generated: ${generationDate}`, 105, yPos, { align: 'center' });

                        // Add Notice of Assignment for Apollo and Tesla only - AFTER thank you
                        if (invoiceModal.companyType === 'apollo' || invoiceModal.companyType === 'tesla') {
                          yPos += 15;
                          
                          doc.setFontSize(11);
                          doc.setFont('helvetica', 'bold');
                          doc.text('NOTICE OF ASSIGNMENT', 105, yPos, { align: 'center' });
                          
                          yPos += 6;
                          doc.setFontSize(10);
                          doc.setFont('helvetica', 'normal');
                          doc.text('THIS INVOICE HAS BEEN ASSIGNED TO,', 105, yPos, { align: 'center' });
                          yPos += 6;
                          doc.text('AND MUST BE PAID DIRECTLY TO:', 105, yPos, { align: 'center' });
                          
                          yPos += 8;
                          doc.setFont('helvetica', 'bold');
                          doc.text('TRIUMPH', 105, yPos, { align: 'center' });
                          
                          yPos += 6;
                          doc.setFont('helvetica', 'normal');
                          doc.text('PO BOX 610028', 105, yPos, { align: 'center' });
                          yPos += 6;
                          doc.text('DALLAS, TX 75261-0028', 105, yPos, { align: 'center' });
                          
                          yPos += 8;
                          doc.text('CLAIMS OR OFFSETS SHOULD BE', 105, yPos, { align: 'center' });
                          yPos += 6;
                          doc.text('DIRECTED TO 866-414-9600', 105, yPos, { align: 'center' });
                          
                          yPos += 10;
                          doc.setFont('helvetica', 'bold');
                          doc.text('THANK YOU FOR YOUR BUSINESS!', 105, yPos, { align: 'center' });
                        }

                        // Get invoice PDF as array buffer
                        const invoicePdfBytes = doc.output('arraybuffer');

                        // Step 2: Create merged PDF with load documents
                        const mergedPdf = await PDFDocument.create();
                        
                        // Add invoice page
                        const invoicePdf = await PDFDocument.load(invoicePdfBytes);
                        const invoicePages = await mergedPdf.copyPages(invoicePdf, invoicePdf.getPageIndices());
                        invoicePages.forEach(page => mergedPdf.addPage(page));

                        // Add load-specific documents - every one of them, PDF or
                        // image. Whatever cannot be embedded is collected and
                        // reported once the invoices are out; the old version
                        // only logged to the console, so accounting had no way
                        // of knowing the BOL never made it into the download.
                        if (loadDocuments && loadDocuments.length > 0) {
                          for (const docItem of loadDocuments) {
                            try {
                              await appendDocumentToInvoicePdf(mergedPdf, docItem);
                            } catch (err) {
                              console.error(`Error adding document ${docItem.name}:`, err);
                              skippedDocuments.push(
                                `${docItem.name || 'Untitled Document'} (load ${load.load_number})`
                              );
                            }
                          }
                        }

                        // Step 3: Save PDF to Supabase storage
                        const mergedPdfBytes = await mergedPdf.save();
                        const pdfBlob = new Blob([new Uint8Array(mergedPdfBytes)], { type: 'application/pdf' });
                        
                        const fileName = `${load.invoice_number}.pdf`;
                        const filePath = `invoices/${fileName}`;

                        // Upload to Supabase storage
                        const { error: uploadError } = await supabase.storage
                          .from('company-documents')
                          .upload(filePath, pdfBlob, { upsert: true });

                        if (uploadError) throw uploadError;

                        // Get public URL
                        const { data: urlData } = supabase.storage
                          .from('company-documents')
                          .getPublicUrl(filePath);

                        // Save record to invoice_pdfs table
                        const { error: dbError } = await supabase
                          .from('invoice_pdfs')
                          .insert({
                            file_name: fileName,
                            invoice_number: load.invoice_number,
                            load_id: load.id,
                            file_path: filePath,
                            file_url: urlData.publicUrl,
                            generated_date: new Date().toISOString().split('T')[0],
                          });

                        if (dbError) throw dbError;

                        // Mark load as invoice_sent
                        await supabase
                          .from('loads')
                          .update({ 
                            invoice_sent: true,
                            invoice_sent_at: new Date().toISOString()
                          })
                          .eq('id', load.id);

                        // Download the PDF to user's computer
                        const downloadUrl = URL.createObjectURL(pdfBlob);
                        const downloadLink = document.createElement('a');
                        downloadLink.href = downloadUrl;
                        downloadLink.download = fileName;
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                        URL.revokeObjectURL(downloadUrl);
                      }

                      // Track company types
                      const newCompanyTypes = new Map(invoiceCompanyTypes);
                      invoiceModal.loads.forEach(load => {
                        if (invoiceModal.companyType) {
                          newCompanyTypes.set(load.id, invoiceModal.companyType);
                        }
                      });
                      setInvoiceCompanyTypes(newCompanyTypes);

                      toast.success(`Generated and downloaded ${invoiceModal.loads.length} separate invoice PDFs!`);

                      if (skippedDocuments.length > 0) {
                        // Longer than the default 5s: this is the one chance to
                        // notice a missing BOL before the invoice is sent.
                        const names = Array.from(new Set(skippedDocuments));
                        toast.warning(
                          `${names.length} document(s) could not be attached and are missing from the invoice: ${names.join(', ')}. Check the file and re-upload it.`,
                          15000
                        );
                      }

                      // Close modal, clear selection, and refresh
                      setInvoiceModal(null);
                      setSelectedLoads(new Set());
                      loadReadyLoads();
                      loadGeneratedInvoicePdfs();
                      loadInvoicedLoads(); // Refresh invoices table to show updated customer
                    } catch (error: any) {
                      console.error('Error creating invoices:', error);
                      toast.error('Failed to create invoices: ' + error.message);
                    }
                  }}
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Download PDF
                </button>
                <button
                  onClick={() => setInvoiceModal(null)}
                  className="text-white hover:text-slate-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto max-h-[calc(90vh-80px)] bg-white" id="invoice-content">
              {/* Show separate invoice preview for each load */}
              {invoiceModal.loads.map((load, loadIndex) => {
                const selectedCompany = invoiceModal.companyType ? companyInfo[invoiceModal.companyType] : Object.values(companyInfo)[0];
                
                const loadDocuments = invoiceModal.documents.filter(doc => doc.load_number === load.load_number);
                
                return (
                  <div key={load.id} className="max-w-3xl mx-auto space-y-6 mb-16 pb-16 border-b-4 border-blue-200 last:border-b-0">
                    {/* Load indicator */}
                    {invoiceModal.loads.length > 1 && (
                      <div className="bg-blue-50 border-l-4 border-blue-600 px-4 py-3 mb-6">
                        <p className="text-sm font-semibold text-blue-900">
                          Invoice {loadIndex + 1} of {invoiceModal.loads.length} - Load #{load.load_number}
                        </p>
                      </div>
                    )}
                    
                    {/* Payable To Section - from selected company's factoring_info; hidden when blank */}
                    {selectedCompany?.factoringInfo && (() => {
                      const lines = selectedCompany.factoringInfo.split('\n').filter(l => l.trim().length > 0);
                      return (
                        <div>
                          <p className="text-sm font-semibold text-slate-700">PAYABLE TO:</p>
                          {lines.map((line, i) => (
                            <p
                              key={i}
                              className={i === 0 ? 'text-lg font-bold text-slate-900' : 'text-sm text-slate-600'}
                            >
                              {line}
                            </p>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Assigned For Section */}
                    <div className="mt-6">
                      <p className="text-sm font-semibold text-slate-700">Assigned For:</p>
                      {selectedCompany ? (
                        <>
                          <p className="text-lg font-bold text-slate-900">{selectedCompany.name}</p>
                          <p className="text-sm text-slate-600">{selectedCompany.address}</p>
                          <p className="text-sm text-slate-600">{selectedCompany.city}, {selectedCompany.state} {selectedCompany.zip}</p>
                          <p className="text-sm text-slate-600">{selectedCompany.country}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-bold text-slate-900">APOLLO FREIGHT SYSTEMS INC</p>
                          <p className="text-sm text-slate-600">5900 S Archer Rd Unit 128</p>
                          <p className="text-sm text-slate-600">Summit, IL 60501</p>
                          <p className="text-sm text-slate-600">United States</p>
                        </>
                      )}
                    </div>

                    {/* Invoice Title */}
                    <div className="text-center my-8">
                      <h1 className="text-4xl font-bold text-slate-900">INVOICE</h1>
                    </div>

                    {/* Multiple Brokers Warning */}
                    {invoiceModal.loadsByBroker && invoiceModal.loadsByBroker.size > 1 && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900">
                          📋 Multiple Brokers Detected ({invoiceModal.loadsByBroker.size} brokers)
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          Separate invoices will be generated for each broker when you click Download PDF.
                        </p>
                      </div>
                    )}

                    {/* Bill To Section - Searchable customer selector per load */}
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">BILL TO:</p>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={showDropdownPerLoad.get(load.id) ? (customerSearchPerLoad.get(load.id) || '') : (selectedCustomersPerLoad.get(load.id) || load.broker_name || 'Unknown Broker')}
                            onChange={(e) => {
                              const newMap = new Map(customerSearchPerLoad);
                              newMap.set(load.id, e.target.value);
                              setCustomerSearchPerLoad(newMap);
                              const showMap = new Map(showDropdownPerLoad);
                              showMap.set(load.id, true);
                              setShowDropdownPerLoad(showMap);
                            }}
                            onFocus={() => {
                              const searchMap = new Map(customerSearchPerLoad);
                              searchMap.set(load.id, '');
                              setCustomerSearchPerLoad(searchMap);
                              const showMap = new Map(showDropdownPerLoad);
                              showMap.set(load.id, true);
                              setShowDropdownPerLoad(showMap);
                            }}
                            placeholder="Search customers..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-semibold text-slate-900"
                          />
                          {showDropdownPerLoad.get(load.id) && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {allCustomers
                                .filter(c => c.name.toLowerCase().includes((customerSearchPerLoad.get(load.id) || '').toLowerCase()))
                                .map(customer => (
                                  <button
                                    key={customer.id}
                                    type="button"
                                    onClick={() => {
                                      const custMap = new Map(selectedCustomersPerLoad);
                                      custMap.set(load.id, customer.name);
                                      setSelectedCustomersPerLoad(custMap);
                                      const showMap = new Map(showDropdownPerLoad);
                                      showMap.set(load.id, false);
                                      setShowDropdownPerLoad(showMap);
                                      const searchMap = new Map(customerSearchPerLoad);
                                      searchMap.set(load.id, '');
                                      setCustomerSearchPerLoad(searchMap);
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-slate-900 border-b border-slate-100 last:border-b-0"
                                  >
                                    {customer.name}
                                  </button>
                                ))}
                              {allCustomers.filter(c => c.name.toLowerCase().includes((customerSearchPerLoad.get(load.id) || '').toLowerCase())).length === 0 && (
                                <div className="px-3 py-2 text-sm text-slate-500 text-center">
                                  No customers found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {selectedCustomersPerLoad.get(load.id) && selectedCustomersPerLoad.get(load.id) !== load.broker_name && (
                          <span className="text-xs text-orange-600 font-semibold whitespace-nowrap">⚠️ Changed</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-2">Rates and charges:</p>
                    </div>

                    {/* Invoice Table - Single Load */}
                    <div className="mt-6">
                      <table className="w-full border-collapse border border-slate-300">
                        <thead>
                          <tr className="bg-slate-50">
                            <th className="border border-slate-300 px-4 py-3 text-left text-sm font-bold text-slate-900">
                              INVOICE Number
                            </th>
                            <th className="border border-slate-300 px-4 py-3 text-left text-sm font-bold text-slate-900">
                              PO Number (reference, load)
                            </th>
                            <th className="border border-slate-300 px-4 py-3 text-left text-sm font-bold text-slate-900">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-slate-300 px-4 py-3 text-sm font-mono align-top">
                              {(() => {
                                const editedNum = editedInvoiceNumbers.get(load.id);
                                const currentValue = editedNum ?? load.invoice_number ?? '';
                                const status = invoiceNumberConflicts.get(load.id);
                                const changed = editedNum !== undefined && editedNum !== (load.invoice_number ?? '');
                                return (
                                  <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={currentValue}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          const nums = new Map(editedInvoiceNumbers);
                                          nums.set(load.id, v);
                                          setEditedInvoiceNumbers(nums);
                                          scheduleInvoiceNumberCheck(load.id, v);
                                        }}
                                        className={`w-36 px-2 py-1 border rounded font-mono focus:ring-2 focus:ring-blue-500 ${
                                          status === 'conflict'
                                            ? 'border-red-400 bg-red-50 focus:border-red-500'
                                            : 'border-slate-300 focus:border-blue-500'
                                        }`}
                                        placeholder="Invoice #"
                                      />
                                      {changed && status !== 'conflict' && (
                                        <span className="text-xs text-orange-600 font-semibold">⚠️ Changed</span>
                                      )}
                                    </div>
                                    {status === 'checking' && (
                                      <span className="text-xs text-slate-500">Checking…</span>
                                    )}
                                    {status === 'conflict' && (
                                      <span className="text-xs font-semibold text-red-600">
                                        This invoice # is already used by another load.
                                      </span>
                                    )}
                                    {status === 'ok' && changed && (
                                      <span className="text-xs text-emerald-600">Available</span>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="border border-slate-300 px-4 py-3 text-sm font-mono">
                              {load.load_number}
                            </td>
                            <td className="border border-slate-300 px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-700">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editedInvoiceAmounts.get(load.id) ?? load.total_rate}
                                  onChange={(e) => {
                                    const newAmount = parseFloat(e.target.value) || 0;
                                    const newMap = new Map(editedInvoiceAmounts);
                                    newMap.set(load.id, newAmount);
                                    setEditedInvoiceAmounts(newMap);
                                  }}
                                  className="w-32 px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {editedInvoiceAmounts.get(load.id) !== load.total_rate && (
                                  <span className="text-xs text-orange-600 font-semibold">⚠️</span>
                                )}
                              </div>
                            </td>
                          </tr>
                          <tr className="bg-slate-50 font-bold">
                            <td className="border border-slate-300 px-4 py-3 text-sm" colSpan={2}>
                              TOTAL
                            </td>
                            <td className="border border-slate-300 px-4 py-3 text-sm">
                              ${(editedInvoiceAmounts.get(load.id) ?? load.total_rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Footer */}
                    <div className="mt-12 text-center">
                      <p className="text-xl font-bold text-slate-900">THANK YOU FOR YOUR BUSINESS!</p>
                      <p className="text-sm text-slate-600 mt-4">
                        Generated: {new Date().toLocaleString('en-US', { 
                          month: '2-digit', 
                          day: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>

                    {/* Notice of Assignment for Apollo and Tesla only - appears AFTER thank you */}
                    {(invoiceModal.companyType === 'apollo' || invoiceModal.companyType === 'tesla') && (
                      <div className="mt-8 text-center space-y-2">
                        <p className="text-sm font-bold text-slate-900">NOTICE OF ASSIGNMENT</p>
                        <p className="text-xs text-slate-700">THIS INVOICE HAS BEEN ASSIGNED TO,</p>
                        <p className="text-xs text-slate-700">AND MUST BE PAID DIRECTLY TO:</p>
                        <p className="text-sm font-bold text-slate-900 mt-2">TRIUMPH</p>
                        <p className="text-xs text-slate-700">PO BOX 610028</p>
                        <p className="text-xs text-slate-700">DALLAS, TX 75261-0028</p>
                        <p className="text-xs text-slate-700 mt-2">CLAIMS OR OFFSETS SHOULD BE</p>
                        <p className="text-xs text-slate-700">DIRECTED TO 866-414-9600</p>
                        <p className="text-sm font-bold text-slate-900 mt-4">THANK YOU FOR YOUR BUSINESS!</p>
                      </div>
                    )}

                    {/* Documents Section for this load */}
                    {loadDocuments.length > 0 && (
                      <div className="mt-12 space-y-8">
                        <div className="page-break"></div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Supporting Documents</h2>
                        
                        {loadDocuments.map((doc, idx) => {
                          const isPDF = doc.mime_type === 'application/pdf' || doc.file_type === 'pdf';
                          const isImage = doc.mime_type?.startsWith('image/');

                          return (
                            <div key={doc.id || idx} className="mb-8 page-break-inside-avoid">
                              {/* Document Header */}
                              <div className="mb-4 pb-2 border-b border-slate-300">
                                <p className="text-sm font-semibold text-slate-700">Load: {doc.load_number}</p>
                                <p className="text-sm text-slate-600">{doc.name}</p>
                              </div>

                          {/* Document Content */}
                          {isPDF ? (
                            <div className="border border-slate-200">
                              <iframe
                                src={doc.url}
                                className="w-full h-[1000px]"
                                title={doc.name}
                              />
                            </div>
                          ) : isImage ? (
                            <div className="border border-slate-200 p-4 bg-white">
                              <img
                                src={doc.url}
                                alt={doc.name}
                                className="w-full h-auto max-h-[1000px] object-contain"
                              />
                            </div>
                          ) : (
                            <div className="p-8 text-center border border-slate-200">
                              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                              <p className="text-slate-600">Document type not supported for preview</p>
                            </div>
                          )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {documentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setDocumentModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Load Documents ({documentModal.documents.length})</h3>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    if (e.target.files) {
                      setUploadFiles(Array.from(e.target.files));
                    }
                  }}
                  className="hidden"
                  id="document-upload"
                />
                <label
                  htmlFor="document-upload"
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold cursor-pointer flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Select Files
                </label>
                {uploadProgress && uploadProgress.current > 0 && (
                  <span className="text-xs text-white/90 truncate max-w-[220px]">
                    Uploading {uploadProgress.current}/{uploadProgress.total}: {uploadProgress.label}
                  </span>
                )}
                {uploadFiles.length > 0 && (
                  <button
                    onClick={handleUploadDocuments}
                    disabled={uploadProgress !== null}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Upload ({uploadFiles.length})
                  </button>
                )}
                <button
                  onClick={() => setDocumentModal(null)}
                  className="text-white hover:text-slate-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {documentModal.documents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-600">No documents found for this load</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {documentModal.documents.map((doc, idx) => {
                    const isPDF = doc.mime_type === 'application/pdf' || doc.file_type === 'pdf';
                    const isImage = doc.mime_type?.startsWith('image/');

                    return (
                      <div key={doc.id || idx} className="border border-slate-200 rounded-lg overflow-hidden">
                        {/* Document Header */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-200">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-slate-900">{doc.name || 'Untitled Document'}</p>
                              <p className="text-xs text-slate-500">{doc.file_type || 'Unknown type'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownloadLoadDocument(doc, doc.id || String(idx))}
                              disabled={downloadingDocId === (doc.id || String(idx))}
                              title="Download without opening"
                              className="px-3 py-1.5 border border-blue-600 text-blue-600 text-sm rounded hover:bg-blue-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                            >
                              {downloadingDocId === (doc.id || String(idx)) ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4" />
                                  Download
                                </>
                              )}
                            </button>
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              Open in New Tab
                            </a>
                          </div>
                        </div>

                        {/* Document Preview */}
                        <div className="bg-white">
                          {isPDF ? (
                            <iframe
                              src={doc.url}
                              className="w-full h-[600px]"
                              title={doc.name}
                            />
                          ) : isImage ? (
                            <img
                              src={doc.url}
                              alt={doc.name}
                              className="w-full h-auto max-h-[600px] object-contain"
                            />
                          ) : (
                            <div className="p-8 text-center">
                              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                              <p className="text-slate-600">Preview not available for this file type</p>
                              <p className="text-xs text-slate-500 mt-1">Click "Open in New Tab" to view</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Note Edit Modal */}
      {noteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <h3 className="text-xl font-bold text-slate-900">Edit Load Note</h3>
            <button
              onClick={() => setNoteModal(null)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6">
            <textarea
              value={editingNotes[noteModal.loadId] !== undefined ? editingNotes[noteModal.loadId] : noteModal.currentNote}
              onChange={(e) => setEditingNotes({ ...editingNotes, [noteModal.loadId]: e.target.value })}
              placeholder="Enter note for this load..."
              rows={8}
              className="w-full px-4 py-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
            <p className="mt-2 text-xs text-slate-500">
              This note will be visible in both Ready for Billing and Invoices tabs
            </p>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
            <button
              onClick={() => setNoteModal(null)}
              className="px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const note = editingNotes[noteModal.loadId] !== undefined ? editingNotes[noteModal.loadId] : noteModal.currentNote;
                handleUpdateNote(noteModal.loadId, note);
                setNoteModal(null);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Save Note
            </button>
          </div>
        </div>
        </div>
      )}

      {/* Load Details Modal */}
      {loadDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setLoadDetailsModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Load Details</h3>
              <button
                onClick={() => setLoadDetailsModal(null)}
                className="text-white hover:text-slate-200 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Load Number</label>
                  <p className="text-base text-slate-900 mt-1">{loadDetailsModal.load_number}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Invoice Number</label>
                  <p className="text-base text-slate-900 mt-1">{loadDetailsModal.invoice_number}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Driver</label>
                  <p className="text-base text-slate-900 mt-1">{loadDetailsModal.driver_name}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Company</label>
                  <p className="text-base text-slate-900 mt-1">{loadDetailsModal.company_name}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Broker</label>
                  <p className="text-base text-slate-900 mt-1">{loadDetailsModal.broker_name}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Pickup Date</label>
                  <p className="text-base text-slate-900 mt-1">
                    {loadDetailsModal.pickup_date ? new Date(loadDetailsModal.pickup_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Amount</label>
                  <p className="text-base font-bold text-green-600 mt-1">
                    ${loadDetailsModal.total_rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Payment Status</label>
                  <p className="text-base text-slate-900 mt-1 capitalize">{loadDetailsModal.payment_status}</p>
                </div>
                {loadDetailsModal.billing_note && (
                  <div className="col-span-2">
                    <label className="text-sm font-semibold text-slate-700">Note</label>
                    <p className="text-base text-slate-900 mt-1 p-3 bg-slate-50 rounded border border-slate-200">
                      {loadDetailsModal.billing_note}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <a
                  href={`/loads/${loadDetailsModal.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  <Eye className="w-4 h-4" />
                  View Full Load Page
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Excel Preview Modal */}
      {excelPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setExcelPreviewModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Excel Preview: {excelPreviewModal.filename}</h3>
              <button
                onClick={() => setExcelPreviewModal(null)}
                className="text-white hover:text-slate-200 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-auto max-h-[calc(90vh-80px)]">
              {/* Generation Date */}
              <div className="mb-4 pb-3 border-b border-slate-200">
                <p className="text-sm font-semibold text-slate-700">
                  Generated: {new Date().toLocaleString('en-US', { 
                    month: '2-digit', 
                    day: '2-digit', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-slate-300">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="border border-slate-300 px-4 py-2 text-left text-sm font-semibold text-slate-700">DTR_NAME</th>
                      <th className="border border-slate-300 px-4 py-2 text-left text-sm font-semibold text-slate-700">INVOICE#</th>
                      <th className="border border-slate-300 px-4 py-2 text-left text-sm font-semibold text-slate-700">INV_DATE</th>
                      <th className="border border-slate-300 px-4 py-2 text-left text-sm font-semibold text-slate-700">PO</th>
                      <th className="border border-slate-300 px-4 py-2 text-right text-sm font-semibold text-slate-700">INVAMT</th>
                      <th className="border border-slate-300 px-4 py-2 text-center text-sm font-semibold text-slate-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelPreviewModal.data.map((row, idx) => {
                      const load = invoicedLoads.find(l => l.invoice_number === row['INVOICE#']);
                      const isReturned = load && returnedInvoices.has(load.id);
                      
                      return (
                        <tr key={idx} className={isReturned ? 'bg-red-50' : (idx % 2 === 0 ? 'bg-white' : 'bg-slate-50')}>
                          <td className="border border-slate-300 px-4 py-2 text-sm">{row.DTR_NAME}</td>
                          <td className="border border-slate-300 px-4 py-2 text-sm">
                            {row['INVOICE#']}
                            {isReturned && <span className="ml-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded">RETURNED</span>}
                          </td>
                          <td className="border border-slate-300 px-4 py-2 text-sm">{row.INV_DATE}</td>
                          <td className="border border-slate-300 px-4 py-2 text-sm">{row.PO}</td>
                          <td className="border border-slate-300 px-4 py-2 text-sm text-right">
                            ${isReturned ? '0.00' : (typeof row.INVAMT === 'number' ? row.INVAMT.toFixed(2) : row.INVAMT)}
                          </td>
                          <td className="border border-slate-300 px-4 py-2 text-center">
                            {load && (
                              <button
                                onClick={async () => {
                                  try {
                                    const newStatus = !isReturned;
                                    const { error } = await supabase
                                      .from('loads')
                                      .update({ excel_returned: newStatus })
                                      .eq('id', load.id);
                                    
                                    if (error) throw error;
                                    
                                    const newReturned = new Set(returnedInvoices);
                                    if (newStatus) {
                                      newReturned.add(load.id);
                                    } else {
                                      newReturned.delete(load.id);
                                    }
                                    setReturnedInvoices(newReturned);
                                    
                                    toast.success(newStatus ? 'Invoice marked as returned' : 'Invoice restored');
                                    loadInvoicedLoads();
                                  } catch (error) {
                                    console.error('Error updating return status:', error);
                                    toast.error('Failed to update invoice status');
                                  }
                                }}
                                className={`px-3 py-1 text-xs rounded font-semibold transition-colors ${
                                  isReturned 
                                    ? 'bg-green-600 text-white hover:bg-green-700' 
                                    : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                              >
                                {isReturned ? 'Restore' : 'Return'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-100 font-semibold">
                    <tr>
                      <td colSpan={4} className="border border-slate-300 px-4 py-2 text-sm text-right">Total:</td>
                      <td className="border border-slate-300 px-4 py-2 text-sm text-right">
                        ${excelPreviewModal.data.reduce((sum, row) => {
                          const load = invoicedLoads.find(l => l.invoice_number === row['INVOICE#']);
                          const isReturned = load && returnedInvoices.has(load.id);
                          return sum + (isReturned ? 0 : (typeof row.INVAMT === 'number' ? row.INVAMT : 0));
                        }, 0).toFixed(2)}
                      </td>
                      <td className="border border-slate-300 px-4 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Number Settings Modal */}
      {showInvoiceRangeSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-slate-700" />
                  Invoice Number Settings
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Set the starting invoice number per company. Each company gets its own window: from its start up to the next-higher company's start, or 100,000 numbers above it when nothing is configured higher. The next number is the highest one <strong>this company</strong> has issued inside its window, plus one &mdash; invoice numbers on other carriers' loads are ignored, as are references typed in by hand (anything with a prefix longer than two letters, such as a broker's <span className="font-mono">LDS…</span>). When a window fills up, numbering restarts at the bottom of it under a letter prefix (A, B, … AA) instead of spilling into the next company.
                </p>
              </div>
              <button
                onClick={() => setShowInvoiceRangeSettings(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {availableCompanies.length === 0 ? (
                <p className="text-sm text-slate-500">No companies loaded yet.</p>
              ) : (
                <div className="space-y-3">
                  {availableCompanies.map(company => {
                    const start = rangeDraft[company.id] ?? '';
                    const stats = invoiceStats[company.id];
                    return (
                      <div key={company.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{company.name}</p>
                            <p className="text-xs text-slate-500">{company.code}</p>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Starting # (From)</label>
                            <input
                              type="number"
                              min={0}
                              value={start}
                              onChange={(e) => setRangeDraft(prev => ({ ...prev, [company.id]: e.target.value }))}
                              placeholder="1000"
                              className="w-36 px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-white rounded border border-slate-200 px-3 py-2">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Invoices created</p>
                            <p className="font-semibold text-slate-900">
                              {loadingStats ? '…' : stats ? stats.count.toLocaleString() : '—'}
                            </p>
                          </div>
                          <div className="bg-white rounded border border-slate-200 px-3 py-2">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Last invoice #</p>
                            <p className="font-semibold text-slate-900">
                              {loadingStats ? '…' : stats && stats.last != null ? stats.last : '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowInvoiceRangeSettings(false)}
                disabled={savingRanges}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Validate
                  const starts: Array<{ name: string; value: number }> = [];
                  for (const company of availableCompanies) {
                    const raw = rangeDraft[company.id] ?? '';
                    if (raw === '') continue;
                    const n = Number(raw);
                    if (!Number.isInteger(n) || n < 0) {
                      toast.error(`${company.name}: use a non-negative whole number`);
                      return;
                    }
                    starts.push({ name: company.name, value: n });
                  }
                  // No two companies may share the exact same start
                  const seen = new Map<number, string>();
                  for (const s of starts) {
                    if (seen.has(s.value)) {
                      toast.error(`${seen.get(s.value)} and ${s.name} share starting # ${s.value}`);
                      return;
                    }
                    seen.set(s.value, s.name);
                  }

                  setSavingRanges(true);
                  try {
                    for (const company of availableCompanies) {
                      const raw = rangeDraft[company.id] ?? '';
                      const start = raw === '' ? null : Number(raw);
                      const { error } = await supabase
                        .from('companies')
                        .update({ invoice_number_start: start })
                        .eq('id', company.id);
                      if (error) throw error;
                    }
                    toast.success('Invoice settings saved');
                    setShowInvoiceRangeSettings(false);
                    await loadCompanyInfo();
                  } catch (err: any) {
                    console.error('Failed to save invoice settings:', err);
                    toast.error(err?.message || 'Failed to save');
                  } finally {
                    setSavingRanges(false);
                  }
                }}
                disabled={savingRanges}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {savingRanges ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
